/**
 * Defines an alternative to dijit/layout/BorderContainer.
 * This BorderContainer is similar to the dijit offering but adds the following features:
 * <ul>
 *    <li>wider splitter hit zones</li>
 *    <li>ability to "collapse" children along the borders</li>
 *    <li>option to display an alternative widget in place of a collapsed child</li>
 *    <li>option to proportionally resize children upon container resize</li>
 *    <li>persistence support</li>
 * </ul>
 *
 * @module MW/layout/BorderContainer
 * @copyright Copyright 2013-2017 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/_base/fx",
    "dojo/_base/window",
    "dojo/dom",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/Evented",
    "dojo/mouse",
    "dojo/on",
    "dojo/touch",
    "dijit/layout/_LayoutWidget",
    "dijit/layout/ContentPane",
    "MW/layout/HidingContainer",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutContainerEvent",
    "MW/layout/LayoutUtils",
    "MW/layout/MaximizingContainer",
    "MW/layout/Restorable",
    "MW/layout/SelectionBlocker",
    "MW/layout/Traversable",
    "mw-log/Log",
    "dojo/text!./templates/CollapsedBorderChildH.html",
    "dojo/text!./templates/CollapsedBorderChildV.html"
], function (declare, event, lang, fx, window, dom, domClass, domConstruct, domGeometry, domStyle, Evented, mouse, on, touch,
             _LayoutWidget, ContentPane, HidingContainer, LayoutChildProperty, LayoutContainerEvent, LayoutUtils,
             MaximizingContainer, Restorable, SelectionBlocker, Traversable, Log, CollapsedTemplateH, CollapsedTemplateV) {

    var DEFAULT_PROPORTION      = 0.25;
	var DEFAULT_MINIMUM_SIZE    = 0;
    var DEFAULT_MAXIMUM_SIZE    = Number.MAX_VALUE;
	var COLLAPSE_THRESHOLD      = 0;  // A non-zero threshold proves problematic because when a child is partially out of view,
                                      // Chrome has a tendency to shift the entire display to accommodate it.
    var MINIMUM_CENTER_SIZE     = 100;
    var SPLITTER_MOVE_THRESHOLD = 3;
    var HEADLINE_TRAVERSAL_ORDER = ["top", "left", "center", "right", "bottom"];
    var SIDEBAR_TRAVERSAL_ORDER  = ["left", "top", "center", "bottom", "right"];

    /**
     * @class MW/layout/BorderContainer
     * @classdesc Defines a layout widget that manages up to 5 children, one in the center surrounded children on each of 4 borders.
     */
    return declare([_LayoutWidget, Evented, Traversable, HidingContainer, MaximizingContainer], {  /** @lends MW/layout/BorderContainer */
        /**
         * @property {string} design - consistent with Dojo BorderContainer:
         *    <ul>
         *    <li>"headline" (default) where the top and bottom extend the full width of the container</li>
         *    <li>"sidebar" where the left and right sides extend from top to bottom</li>
         *    </ul>
         *    Note that a per child "layoutOrder" property may be used to customize or override the "design".
         *
         * @property {boolean} animationEnabled - true if collapse, expand and restore operations should be animated.
         *
         * @instance
         */
        _: undefined,  // This is here just to trick the JSDoc parser into presenting the above properties in a single
                       // table without making it look they are children of the first "member" below
        design: "headline",
        animationEnabled: true,
        minimumCenterWidth: MINIMUM_CENTER_SIZE,
        minimumCenterHeight: MINIMUM_CENTER_SIZE,
        baseClass: "borderContainer",
        _activeAnimationCount: 0,
        _resizeDragCount: 0,

        /* Properties related to the center widget
        _centerWidget;
        _centerProperties;
        _centerSize;
        _preExpandCenterSize;
        _isCenterProportional;
        _isCenterExpanded;
        _regionsCollapsedUponCenterExpand;
        _collapsedCenterWidget;
        */

        /* Stores the following per border region:
        _borderInfo,
            region
            widget
            properties
            initComplete
            hasSplitter
            isResizable
            minSize
            minAutoSize
            maxSize
            isCollapsible
            layoutOrder
            isProportional
            proportion
            splitterOrientation
            splitterNode
            splitterThickness
            visibleSplitterThickness
            preSplitterOverlap
            postSplitterOverlap
            initialDimension
            freeDimension
            preCollapseFreeDimension
            unconstrainedFreeDimension
            followOpposite
            isCollapsed
            isCollapsing
            isExpanded
            isRestoring
            regionsCollapsedUponExpand
            restoredSplitterPosition
            restoredReferenceDimension
        */

        /* Stores the following for each border region being resized
        _dragInfo,
            borderInfo
            startPoint
            lastPoint
            initialPosition
            splitterPosition
            collapsePosition
            lowerLimit
            upperLimit
            connections
            hasSplitterMoved
            animation
            done
        */

        constructor: function () {
            this._borderInfo = {};
            this._dragInfo = {};
        },

        buildRendering: function () {
            this.inherited(arguments);
            domClass.add(this.domNode, "mwLayoutContainer");
        },

        startup: function () {
            if (this._started) {return; }

            var that = this;
            this.getChildren().forEach(function (child) {
                that._setupChild(child);
            });

            this.inherited(arguments);
        },

        /**
         * Adds a child to the border container.  The child will be managed in accordance with the
         * provided properties.  If the properties object is Stateful then the container will update the values
         * of properties designated as "dynamic" below.  It will also respond to changes made to these properties.
         * Hence, for example, if the user drags a splitter so as to collapse a child the corresponding isCollapsed
         * property will be set true by the container.  Conversely if the some code outside the container sets the
         * isCollapsed property true the container will respond by collapsing the corresponding child.
         *
         * @param widget {_Widget} the child to add
         * @param properties {Object} an object containing any of the following properties:
         * @param properties.region {string} with value "top", "left", "bottom", "right" or "center"
         *        Determines the placement of the child within the container.  When the "region" is
         *        "center" the remaining properties are not applicable.
         * @param properties.hasSplitter {boolean} default=false
         *        Determines whether a splitter will appear adjacent to the child
         * @param properties.isResizable {boolean} default=hasSplitter
         *        Determines whether the user can drag the splitter
         * @param properties.minSize {number}
         *        Lower bound on the child's "free" (within the container) dimension in pixels
         * @param properties.minAutoSize {number}
         *        Lower bound on the child's "free" (within the container) dimension in pixels.  Applied when the
         *        child is resized during container resize but not when the child is resized explicitly via splitter drag.
         * @param properties.maxSize {number}
         *        Upper bound on the child's "free" (within the container) dimension in pixels
         * @param properties.isCollapsible {boolean} default=false
         *        Determines whether the child will "collapse" to its adjacent border when its splitter
         *        is moved sufficiently close
         * @param properties.isCollapsed {boolean} true if the child is collapsed (this property is dynamic)
         * @param properties.isExpanded {boolean} true if the child is expanded (this property is dynamic)
         * @param properties.layoutOrder {number} default = 0 or 1 depending on container "design" and child "region"
         *        Specifies the relative layout order of the children.  Lower numbered children are
         *        laid out first.
         * @param properties.isProportional {boolean} default=false
         *        Specifies whether the child's free dimension should scale with the container size
         * @param properties.proportion {number} in range 0.0 to 0.5
         *        Specifies the initial proportion of the relevant container dimension allocated to the child
         *        If a proportion is not specified the relevant CSS style attribute (width or height) will be
         *        used to determine the initial dimension.  If the style attribute isn't specified a default
         *        proportion will be used.
         * @param properties.collapsedWidget {_Widget} - Another widget that will be displayed in place of the child being
         *        added when it is collapsed.  If a collapsedWidget is not specified a narrow gray bar with a grip
         *        will appear by default.
         * @param properties.getCollapsedWidget {function} function that provides the widget to be displayed in place of the
         *        center child when it is collapsed (for example when a border child is expanded).
         * @param properties.getCollapsedSize (function) function that returns the dimensions of the collapsed widget
         *  @instance
         */
        addWithProperties: function (widget, properties) {
            var region = properties.region;
            var borderInfo;
            Log.assert(region, "No region specified for " + widget.id);

            widget.region = region;
            if (region === "center") {
                this._bindCenterProperties(properties);
                this._isCenterProportional = properties.isProportional;
            } else {
                borderInfo = this._createBorderInfo(region, widget);
                this._bindChildProperties(borderInfo, properties);
                if (properties === widget) {
                    this._getInfoFromWidget(widget, borderInfo);
                } else {
                    lang.mixin(borderInfo, properties);
                    if (properties.isResizable === undefined) {
                        borderInfo.isResizable = borderInfo.hasSplitter;
                    }
                }
                this._borderInfo[region] = borderInfo;
            }
            this.addChild(widget);
        },

        /**
         * This is a less flexible alternative to the addWithProperties function.
         * It is compatible with the Dijit BorderContainer.  Properties related to the container's handling of the child,
         * namely region, splitter, resizable, collapsible, layoutOrder, minSize, minAutoSize and maxSize will be obtained directly from the
         * widget.  Note that this is a subset of the properties supported by addWithProperties and further that some
         * of the names are slightly different for compatibility.
         *
         * This method overrides _LayoutWidget.addChild
         *
         * @param widget {_Widget} the child to add
         * @instance
         */
        addChild: function (widget) {
            var region = widget.region;
            var borderInfo;
            Log.assert(region, "No region specified for " + widget.id);

            if (region === "center") {
                // Assertion commented out since rich text editor triggers it
                // Log.assert(!this._centerWidget, "center region already occupied");
                this._centerWidget = widget;
                this._bindCenterProperties(widget);
                this._clearExpandedStates(); // See geck 2019236
            } else if (this._isBorderRegion(region)) {
                borderInfo = this._borderInfo[region];
                if (borderInfo) {
                    Log.assert(borderInfo.widget.getParent() !== this, region + " region already occupied");
                } else {
                    borderInfo = this._createBorderInfo(region, widget);
                    this._borderInfo[region] = borderInfo;
                    this._getInfoFromWidget(widget, borderInfo);
                }
                this._bindChildProperties(borderInfo, widget);
            }
            this.inherited(arguments);
            if (this._started) {
                if (this._pendingState && this._isPopulatedPerState(this._pendingState)) {
                    this._restoreState(this._pendingState);
                    delete this._pendingState;
                } else {
                    this.layout();
                }
                this.emit(LayoutContainerEvent.STATE_CHANGED);
            }
            if (region === "center") {
                this._updateExpandedStates();
            }
        },

        _setupChild: function (child) {

            var region = child.region;
            var borderInfo;
            var lineNode;
            var thicknessProperty;
            var paddingProperty;
            var saveAnimation, savePosition, saveReference;

            this.inherited(arguments);

            domStyle.set(child.domNode, "position", "absolute");

            if (!region || child === this._collapsedCenterWidget) {
                return;
            }
            borderInfo = this._borderInfo[region];
            if (borderInfo) {
                if (child === borderInfo.collapsedWidget) {
                    return;
                }
                borderInfo.initComplete = true;
            } else {
                this._initialCenterWidth  = child.domNode.style.width;
                this._initialCenterHeight = child.domNode.style.height;
            }

            // Add a splitter if called for
            if (borderInfo && borderInfo.hasSplitter) {
                thicknessProperty = borderInfo.splitterOrientation === "H" ? "height" : "width";
                paddingProperty = borderInfo.splitterOrientation === "H" ? "padding-top" : "padding-left";
                if (borderInfo.isResizable) {
                    // If the child is resizable store its initial size preference
                    borderInfo.initialDimension = this._getDimensionFromStyle(borderInfo);
                    borderInfo.splitterNode = domConstruct.create("div", {id : child.domNode.id + "Splitter"}, this.domNode);
                    domClass.add(borderInfo.splitterNode, "splitter" + borderInfo.splitterOrientation);
                    dom.setSelectable(borderInfo.splitterNode, false);
                    LayoutUtils.preventFocus(borderInfo.splitterNode, true);

                    lineNode = domConstruct.create("div", {id : child.domNode.id + "SplitterLine"}, borderInfo.splitterNode);
                    domClass.add(lineNode, "splitterLine" + borderInfo.splitterOrientation);
                    dom.setSelectable(lineNode, false);
                    LayoutUtils.preventFocus(lineNode, true);

                    borderInfo.splitterThickness        = Math.round(domStyle.get(borderInfo.splitterNode, thicknessProperty));
                    borderInfo.visibleSplitterThickness = Math.round(domStyle.get(lineNode, thicknessProperty));
                    borderInfo.preSplitterOverlap       = Math.round(domStyle.get(borderInfo.splitterNode, paddingProperty));
                    borderInfo.postSplitterOverlap      = borderInfo.splitterThickness - borderInfo.visibleSplitterThickness - borderInfo.preSplitterOverlap;

                    if (borderInfo.followOpposite) {
                        // Follow opposite may have been set during state restoration (prior to child add)
                        domStyle.set(borderInfo.splitterNode, {visibility: "hidden"});
                    }

                    // Connect listener's to support drag
                    var beginDragFunction = lang.hitch(this, "_beginSplitterDrag", borderInfo);
                    on(borderInfo.splitterNode, touch.press, beginDragFunction);
                    on(lineNode, touch.press, beginDragFunction);

                    var handleDoubleClickFunction = lang.hitch(this, "_handleDoubleClick", borderInfo);
                    on(borderInfo.splitterNode, "dblclick", handleDoubleClickFunction);
                    on(lineNode, "dblclick", handleDoubleClickFunction);

                    var suppressContextMenuFunction = function (event) {event.preventDefault(); };
                    on(borderInfo.splitterNode, "contextmenu", suppressContextMenuFunction);
                    on(lineNode, "contextmenu", suppressContextMenuFunction);
                } else {
                    // Not resizable
                    borderInfo.splitterNode = domConstruct.create("div", {id : child.domNode.id + "Splitter"}, this.domNode);
                    domStyle.set(borderInfo.splitterNode, "position", "absolute");
                    domClass.add(borderInfo.splitterNode, "splitterLine" + borderInfo.splitterOrientation);
                    dom.setSelectable(borderInfo.splitterNode, false);
                    LayoutUtils.preventFocus(borderInfo.splitterNode);

                    borderInfo.splitterThickness        = Math.round(domStyle.get(borderInfo.splitterNode, thicknessProperty));
                    borderInfo.visibleSplitterThickness = borderInfo.splitterThickness;
                    borderInfo.preSplitterOverlap       = 0;
                    borderInfo.postSplitterOverlap      = 0;
                }
                if (typeof borderInfo.restoredSplitterPosition === "function") {
                    borderInfo.restoredSplitterPosition = borderInfo.restoredSplitterPosition();
                }
                if (borderInfo.collapsePending) {
                    saveAnimation = this.animationEnabled;
                    this.animationEnabled = false;
                    savePosition = borderInfo.restoredSplitterPosition;
                    saveReference = borderInfo.restoredReferenceDimension;
                    this._collapse(borderInfo);
                    borderInfo.restoredSplitterPosition = savePosition;
                    borderInfo.restoredReferenceDimension = saveReference;
                    this.animationEnabled = saveAnimation;
                    delete borderInfo.collapsePending;
                }
            }
        },

        /**
         * This method returns the child currently occupying a specified region
         *
         * @param region {string} the region of interest
         * @returns {_Widget} the child widget occupying the region
         * @instance
         */
        getChildInRegion: function (region) {
            if (region === "center" && this._centerWidget) {
                return this._centerWidget;
            } else {
                var borderInfo = this._borderInfo[region];
                if (borderInfo) {
                    return borderInfo.widget;
                }
            }
            return null;
        },

        /**
         * This method removes the child currently occupying a specified region and returns it.
         *
         * @param region {string} the region from which to remove
         * @returns {_Widget} the removed child widget
         * @instance
         */
        removeChildInRegion: function (region) {
            var child = this.getChildInRegion(region);
            if (child) {
                this.removeChild(child);
            }
            return child;
        },

        /**
         * This method overrides _LayoutWidget.removeChild
         *
         * @param widget {_Widget} the widget to remove
         * @instance
         */
        removeChild: function (widget) {
            var region = widget.region;
            var borderInfo;
            var restoreStyle = {
                top: "auto",
                left: "auto",
                position: "static",
                clip: "auto"
            };
            if (region === "center") {
                if (this._collapsedCenterWidget) {
                    // Remove and destroy the collapsed center widget
                    _LayoutWidget.prototype.removeChild.apply(this, [this._collapsedCenterWidget]);
                    this._collapsedCenterWidget.destroyRecursive();
                    delete this._collapsedCenterWidget;
                }
                this._centerWidget = null;
                if (this._unbindCenterProperties) {
                    this._unbindCenterProperties();
                }
                restoreStyle.width  = this._initialCenterWidth;
                restoreStyle.height = this._initialCenterHeight;
            } else {
                // Remove the splitter and border info
                borderInfo = this._borderInfo[region];
                if (borderInfo) {
                    if (borderInfo.unbindChildProperties) {
                        borderInfo.unbindChildProperties();
                    }
                    if (borderInfo.initialDimension) {
                        if (borderInfo.splitterOrientation === "H") {
                            restoreStyle.height = borderInfo.initialDimension;
                        } else {
                            restoreStyle.width  = borderInfo.initialDimension;
                        }
                    }
                    if (borderInfo.splitterNode) {
                        domConstruct.destroy(borderInfo.splitterNode);
                    }
                    if (borderInfo.isCollapsed) {
                        _LayoutWidget.prototype.removeChild.apply(this, [borderInfo.collapsedWidget]);
                    }
                    if (borderInfo.collapsedWidget && borderInfo.collapsedWidget.isDefaultCollapsedWidget) {
                        borderInfo.collapsedWidget.destroyRecursive();
                    }
                    delete this._borderInfo[region];
                }
            }
            this.inherited(arguments);

            var dragInfo = this._dragInfo[region];
            if (dragInfo) {
                if (dragInfo.animation) {
                    dragInfo.animation.stop();
                }
                delete this._dragInfo[region];
            }

            if (borderInfo || region === "center") {
                if (this._started) {
                    this.layout();
                    this.emit(LayoutContainerEvent.STATE_CHANGED);
                }

                // Clean up whatever style changes we made to the child
                domStyle.set(widget.domNode, restoreStyle);
            }
        },

        /**
         *  Collapses the specified region
         *
         * @param region {string} the region to collapse
         * @instance
         */
        collapse: function (region) {
            var borderInfo = this._borderInfo[region];
            if (borderInfo) {
                this._collapse(borderInfo);
            }
        },

        /**
         * @param region {string} the region whose collapsed state is being requested
         * @returns {boolean} true if the specified region is collapsed
         * @instance
         */
        isCollapsed: function (region) {
            var borderInfo = this._borderInfo[region];
            return borderInfo ? !!borderInfo.isCollapsed : !!this._collapsedCenterWidget;
        },

        /**
         * @param region {string} the region whose collapsing statusis being requested
         * @returns {boolean} true if the specified region is in the process of collapsing
         * @instance
         */
        isCollapsing: function (region) {
            var borderInfo = this._borderInfo[region];
            return borderInfo ? !!borderInfo.isCollapsing : false;
        },

        /**
         * Expands the child appearing within the specified region to occupy as much of the container as possible
         * given the size and collapse constraints of other children.  Note that "expand" is not the reverse
         * of "collapse".  "restore" reverses the effect of either collapse or expand.
         *
         * @param region {string} the region to be expanded.
         * @instance
         */
        expand: function (region) {
            var i;
            var regions;
            var collapsedRegions = [];
            var otherRegion, collapseOther;
            regions = Object.keys(this._borderInfo);
            for (i = 0; i < regions.length; i = i + 1) {
                otherRegion = regions[i];
                if (otherRegion !== region && this._borderInfo[otherRegion].isCollapsible && !this._borderInfo[otherRegion].isCollapsed) {
                    this._collapse(this._borderInfo[otherRegion]);
                    collapsedRegions.push(otherRegion);
                }
            }
            if (region !== "center") {
                var borderInfo = this._borderInfo[region];
                // If the region to be expanded is currently collapsed, first restore it
                if (borderInfo.isCollapsed) {
                    this._setCollapsed(borderInfo, false);
                }
                borderInfo.regionsCollapsedUponExpand = collapsedRegions;

                // Drag (with animation) the splitter between the center and the expanding region
                // until the center has reached its minimum size
                var dragInfo = this._dragInfo[region];
                if (!dragInfo) {
                    dragInfo = this._initDragInfo(borderInfo, 0, 0);
                    this._dragInfo[region] = dragInfo;
                }
                dragInfo.done = true;
                this._doExpand(dragInfo);
            } else {
                this._setCenterExpanded(true);
                this._regionsCollapsedUponCenterExpand = collapsedRegions;
            }
        },

        /**
         * @param region {string} the region whose expand state is being requested
         * @returns {boolean} true if the specified region is expanded
         * @instance
         */
        isExpanded: function (region) {
            if (region === "center") {
                return this._isCenterExpanded;
            } else {
                return this._borderInfo[region] && this._borderInfo[region].isExpanded;
            }
        },

        /**
         * Restores the collapsed or expanded child appearing within the specified region to its pre expanded/collapsed size
         *
         * @param region {string} the region to be restored.
         * @param fromRegion {string} only applicable when restoring center from collapsed.  Identifies the approximate region
         *                            of the collapsed center.
         * @instance
         */
        restore: function (region, fromRegion) {
            this._inRestore = true;
            var regions, i, isExpanded, collapsedRegions, otherRegion, borderInfo;
            if (region === "center") {
                isExpanded = this._isCenterExpanded;
                collapsedRegions = this._regionsCollapsedUponCenterExpand;
                this._setCenterExpanded(false);
                this._regionsCollapsedUponCenterExpand = null;
            } else {
                borderInfo = this._borderInfo[region];
                if (borderInfo && borderInfo.isExpanded) {
                    isExpanded = true;
                    collapsedRegions = borderInfo.regionsCollapsedUponExpand;
                    borderInfo.regionsCollapsedUponExpand = null;
                    borderInfo.isRestoring = true;
                }
            }

            if (isExpanded) {
                regions = Object.keys(this._borderInfo);
                for (i = 0; i < regions.length; i = i + 1) {
                    otherRegion = regions[i];
                    if (otherRegion !== region && (!collapsedRegions || collapsedRegions.indexOf(otherRegion) !== -1)) {
                        this._restore(this._borderInfo[regions[i]]);
                    }
                }
            } else if (region === "center" && this._isCenterCollapsed()) {
                this._restoreCenter(fromRegion);
            }
            if (borderInfo) {
                this._restore(borderInfo);
            }
            this._inRestore = false;
        },

        /*
         * Part of the MaximizingContainer interface
         */
        canMaximizeLocally: function (child) {
            var region = child.region;
            var regions, i, borderInfo;
            if (region) {
                if (this.isExpanded(region)) {
                    return false;
                }
                regions = Object.keys(this._borderInfo);
                for (i = 0; i < regions.length; i++) {
                    borderInfo = this._borderInfo[regions[i]];
                    if (borderInfo.isCollapsible && !borderInfo.isCollapsed) {
                        return true;
                    }
                }
                return false;
            }
            return false;
        },

        /*
         * Part of the MaximizingContainer interface
         */
        canRestoreLocally: function (child) {
            var region = child.region;
            if (region) {
                // Can only be restored if it was previously expanded and there are currently collapsed borders
                return this.isExpanded(region) && (Object.keys(this._borderInfo).length > 0);
            }
            return false;
        },

        /*
         * Part of the MaximizingContainer interface
         */
        maximizeLocally: function (child) {
            var region = child.region;
            if (region) {
                this.expand(region);
            }
        },

        /*
         * Part of the MaximizingContainer interface
         */
        restoreLocally: function (child) {
            var region = child.region;
            if (region) {
                this.restore(region);
            }
        },

        /**
         * This is effectively a restricted version of restore, that will restore the border region containing the
         * specified widget if it is collapsed but not if it is expanded.  This is part of the Traversable API.
         *
         * @param widget {_Widget} the widget to reveal
         * @instance
         */
        revealChild: function (widget) {
            var region = widget.region;
            if (this.isCollapsed(region)) {
                this.restore(region);
            }
        },

        /**
         * Determines the next child in a top->bottom, left->right traversal order.  This is part of the Traversable API.
         *
         * @param widget {_Widget} the reference child widget
         * @param showing {boolean} if true then return a child that is already showing
         * @returns {_Widget} the next child widget
         * @instance
         */
        nextChild: function (widget, showing) {
            if (this.getChildren().length === 0) {return null;}
            var order = this.traversalOrder || (this.design === "headline" ? HEADLINE_TRAVERSAL_ORDER : SIDEBAR_TRAVERSAL_ORDER);
            var lastRegion = order[order.length-1];
            // If the widget is the lastChild, this check would prevent the function from returning the firstChild in case
            // they have a parent.
            if (widget && widget.region === lastRegion && this.getParent()) {
                return null;
            }
            var widgetIndex = widget ? order.indexOf(widget.region) : -1;
            var index, nextRegion, nextInfo;
            for (index = (widgetIndex + 1) % order.length; index !== widgetIndex; index = (index + 1) % order.length) {
                nextRegion = order[index];
                if (nextRegion === "center" && this._centerWidget) {
                    return this._centerWidget;
                }
                nextInfo = this._borderInfo[nextRegion];
                if (nextInfo && !(showing && nextInfo.isCollapsed)) {
                    if (nextInfo.widget.nextChild) {
                        if (nextInfo.widget.nextChild(null, showing)) {
                            return nextInfo.widget;
                        }
                    }
                    else {
                        return nextInfo.widget;
                    }
                }

                // Return null if no child is found and the nextRegion is the lastRegion.
                if ((!nextInfo || (showing && nextInfo.isCollapsed)) && nextRegion === lastRegion && this.getParent()) {
                    return null;
                }
            }
            return null;
        },

        /**
         * Determines the previous child in a top->bottom, left->right traversal order.  This is part of the Traversable API.
         *
         * @param widget {_Widget} the reference child widget
         * @param showing {boolean} if true then return a child that is already showing
         * @returns {_Widget} the previous child widget
         * @instance
         */
        previousChild: function (widget, showing) {
            if (this.getChildren().length === 0) {return null;}
            var order = this.traversalOrder || (this.design === "headline" ? HEADLINE_TRAVERSAL_ORDER : SIDEBAR_TRAVERSAL_ORDER);
            var lastRegion = order[0];
            // If the widget is the firstChild, this check would prevent the function from returning the lastChild in case
            // they have a parent.
            if (widget && widget.region === lastRegion && this.getParent()) {
                return null;
            }
            var widgetIndex = widget ? order.indexOf(widget.region) : order.length;
            var index, prevRegion, prevInfo;
            for (index = (widgetIndex - 1 + order.length) % order.length; index !== widgetIndex; index = (index - 1) % order.length) {
                prevRegion = order[index];
                if (prevRegion === "center" && this._centerWidget) {
                    return this._centerWidget;
                }
                prevInfo = this._borderInfo[prevRegion];
                if (prevInfo && !(showing && prevInfo.isCollapsed)) {
                    if (prevInfo.widget.previousChild) {
                        if (prevInfo.widget.previousChild(null, showing)) {
                            return prevInfo.widget;
                        }
                    }
                    else {
                        return prevInfo.widget;
                    }
                }

                // Return null if no child is found and the prevRegion is the lastRegion.
                if ((!prevInfo || (showing && prevInfo.isCollapsed)) && prevRegion === lastRegion && this.getParent()) {
                    return null;
                }
            }
            return null;
        },

        /** This fulfills the HidingContainer interface
         * @instance
         */
        isShowingLocally: function (widget) {
            return widget.getParent() === this && !this.isCollapsed(widget.region) && !this.isCollapsing(widget.region);
        },

        /**
         * Updates the container in response to resize.
         *
         * This method overrides the inherited resize method
         *
         * @instance
         */
        resize: function (/* newSize, currentSize */) {
            var i, regions, region, dragInfo, contentBox, change;
            // Cache the computed style
            if (!this.computedStyle) {
                this.computedStyle = domStyle.getComputedStyle(this.domNode);
            }
            this.inherited(arguments);
            if (this._isCenterProportional === false) {
                this._portionsAreStale = true;
            }

            // If a collapse or expand is in progress, adjust the target positions
            regions = Object.keys(this._dragInfo);
            if (regions.length > 0) {
                contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
            }
            for (i = 0; i < regions.length; i = i + 1) {
                region = regions[i];
                dragInfo = this._dragInfo[region];
                if (dragInfo.animation) {
                    switch (region) {
                    case "left":
                        if (contentBox.w !== dragInfo.lastReferenceDimension) {
                            change = (contentBox.w - dragInfo.lastReferenceDimension);
                            dragInfo.oppositeCollapsePosition += change;
                            dragInfo.upperLimit += change;
                            dragInfo.lastReferenceDimension = contentBox.w;
                        }
                        break;
                    case "top":
                        if (contentBox.h !== dragInfo.lastReferenceDimension) {
                            change = (contentBox.h - dragInfo.lastReferenceDimension);
                            dragInfo.oppositeCollapsePosition += change;
                            dragInfo.upperLimit += change;
                            dragInfo.lastReferenceDimension = contentBox.h;
                        }
                        break;
                    case "right":
                        if (contentBox.w !== dragInfo.lastReferenceDimension) {
                            change = (contentBox.w - dragInfo.lastReferenceDimension);
                            dragInfo.collapsePosition += change;
                            dragInfo.upperLimit += change;
                            dragInfo.lastReferenceDimension = contentBox.w;
                        }
                        break;
                    case "bottom":
                        if (contentBox.h !== dragInfo.lastReferenceDimension) {
                            change = (contentBox.h - dragInfo.lastReferenceDimension);
                            dragInfo.collapsePosition += change;
                            dragInfo.upperLimit += change;
                            dragInfo.lastReferenceDimension = contentBox.h;
                        }
                        break;
                    }
                }
            }
        },

        /**
         * Returns the "free" dimension, i.e that which is directly adjustable, in pixels of the child occupying the given region.
         *
         * @param region {string} the region whose dimension is requested
         * @returns {number} the dimension of the child in question
         * @instance
         */
        getFreeDimension: function (region) {
            var borderInfo = this._borderInfo[region];
            return borderInfo && borderInfo.freeDimension ? borderInfo.freeDimension : 0;
        },

        /**
         * Sets the "free" dimension in pixels of the child occupying the given region.
         *
         * @param region {string} the region whose dimension is to be set
         * @param dimension {number} the dimension in pixels
         * @instance
         */
        setFreeDimension: function (region, dimension) {
            var borderInfo = this._borderInfo[region];
            if (borderInfo) {
                var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
                var overhead   = this._computeSplitterOverhead();
                var availDimension = borderInfo.splitterOrientation === "H" ? contentBox.h - overhead.h
                                                                            : contentBox.w - overhead.w;
                if (availDimension > 0) {
                    var defaultDimension = Math.floor(availDimension * DEFAULT_PROPORTION);
                    if (dimension >= availDimension) {
                        dimension = Math.max(availDimension - MINIMUM_CENTER_SIZE, defaultDimension);
                    } else if (dimension < 0) {
                        dimension = defaultDimension;
                    }
                }
                if (borderInfo.isCollapsed) {
                    borderInfo.preCollapseFreeDimension = dimension;
                } else {
                    borderInfo.freeDimension = dimension;
                }
                if (borderInfo.isProportional) {
                    borderInfo.proportion = availDimension > 0 ? (borderInfo.freeDimension / availDimension) : 1;
                }
                if (this._started) {
                    this.layout();
                }
            }
        },

        /**
         * Similar to getFreeDimension but if the region in question is collapsed returns the free dimension
         * prior to its collapse.
         *
         * @param region {string} the region whose dimension is requested
         * @returns {number} the dimension of the child in question
         * @instance
         */
        getNonCollapsedFreeDimension: function (region) {
            var borderInfo = this._borderInfo[region];
            if (borderInfo && borderInfo.isCollapsed && borderInfo.preCollapseFreeDimension) {
                return borderInfo.preCollapseFreeDimension;
            } else {
                return this.getFreeDimension(region);
            }
        },

        /**
         * Returns the center size prior to its collapse, due to expansion of a border region or due to drag
         *
         * @returns {number} the dimension of the center region
         * @instance
         */
        getNonCollapsedCenterSize: function () {
            return this._preCollapseCenterSize;
        },

        /**
         * Returns the proportion of the container width or height occupied by a child along its free dimension
         *
         * @param region {string} the region whose proportion is requested
         * @returns {number} the fraction of the total container dimension occupied by the child in the specified region
         * @instance
         */
        getProportion: function (region) {
            return this._getProportion(this._borderInfo[region]);
        },

        /**
         * Set the proportion of the container width or height occupied by the child in the given region.
         *
         * @param region {string} the region whose proportion to be set
         * @param {number} proportion the fraction of the total container dimension to be occupied by the child in the specified region
         * @instance
         */
        setProportion: function (region, proportion) {
            var borderInfo = this._borderInfo[region];
            if (borderInfo) {
                if (proportion < 0 || proportion >= 1) {
                    proportion = DEFAULT_PROPORTION;
                }
                borderInfo.proportion = proportion;
                if (this._started) {
                    this.layout();
                }
            }
        },

        /**
         * Returns an object that encapsulates the container's user modifiable state, including the
         * position of movable splitters and which, if any, borders are collapsed.
         * This object can subsequently be passed to the setState function in order to restore the container
         * to a previous state.  This is part of the Restorable API.
         *
         * @returns {Object} object encapsulating the current state
         * @instance
         */
        getState: function () {
            var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
            var state = {
                referenceWidth: contentBox.w,
                referenceHeight: contentBox.h,
                centerExpanded: !!this._isCenterExpanded,
                centerCollapsed: this._isCenterCollapsed()
            };
            if (state.centerCollapsed) {
                state.centerCollapseEdge = this._collapsedCenterWidget.__edge;
                state.preCollapseCenterSize = this._preCollapseCenterSize;
            }
            var borderInfo;
            var that = this;
            Object.keys(this._borderInfo).forEach(function (region) {
                borderInfo = that._borderInfo[region];
                if (borderInfo.isResizable) {
                    state[region] = that._getBorderState(borderInfo, region === "left" || region === "right" ? contentBox.w : contentBox.h);
                }
            });
            return state;
        },

        /**
         * Restores the container to a previously saved state (see getState).  If the container does not currently have
         * children in the locations that were occupied when the state was captured the actual restoration will
         * be deferred until such time as any missing children have been added.  This is part of the Restorable API.
         *
         * @param state {Object} object encapsulating previous state
         * @instance
         */
        setState: function (state) {
            if (this._isPopulatedPerState(state)) {
                this._restoreState(state);
            } else {
                this._pendingState = state;
            }
        },

        /**
         * This is part of the Restorable API.
         *
         * @returns {boolean} true if the container is in the process of restoring state.
         * @instance
         */
        isRestoringState: function () {
            return this._pendingState !== undefined;
        },

        /**
         * Ends the restore process initiated by a previous setState call.  endStateRestore is called automatically when
         * restoration is complete but may also be called explicitly to prematurely terminate restoration.
         *
         * This is part of the Restorable API.
         *
         * @instance
         */
        endStateRestore: function () {
            delete this._pendingState;
        },

        /**
         * Returns true if the container currently contains resizeable children in the same regions specified in the
         * given state object
         *
         * @private
         * @instance
         */
        _isPopulatedPerState: function (state) {
            var isPopulated = true;
            var regions     = ["left", "right", "top", "bottom"];
            var i, region, hasResizable;
            for (i = 0; i < regions.length && isPopulated; i = i + 1) {
                region = regions[i];
                hasResizable = !!this._borderInfo[region] && this._borderInfo[region].isResizable;
                if (hasResizable !== (state[region] !== undefined)) {
                    isPopulated = false;
                }
            }
            return isPopulated;
        },

        _restoreState: function (state) {
            var contentBox  = domGeometry.getContentBox(this.domNode, this.computedStyle);
            var isSameWidth = state.referenceWidth === contentBox.w;
            var isSameHeight = state.referenceHeight === contentBox.h;
            var that = this;
            // Restore the center collapsed state
            if (this._isCenterCollapsible() && state.centerCollapsed !== undefined && state.centerCollapsed !== this._isCenterCollapsed()) {
                if (state.centerCollapsed) {
                    this._switchToCollapsedCenter(state.centerCollapseEdge);
                    this._preCollapseCenterSize = state.preCollapseCenterSize;
                } else {
                    this._switchToNormalCenter();
                }
            }
            this._isCenterExpanded = state.centerExpanded;

            // Update the layout constraints now so that they the any free dimensions that are set won't be wiped out
            // when the constraints are updated from the layout function.
            this._updateLayoutConstraints();
            Object.keys(this._borderInfo).forEach(function (region) {
                if (state[region]) {
                    if (that._borderInfo[region].splitterOrientation === "H") {
                        that._restoreBorderState(region, state[region], isSameHeight, state.referenceHeight);
                    } else {
                        that._restoreBorderState(region, state[region], isSameWidth, state.referenceWidth);
                    }
                }
            });
        },

        _getBorderState: function (borderInfo, referenceDimension) {
            var state = {
                freeDimension: borderInfo.isCollapsed
                                  ? this._computeFreeDimensionFromSplitterPosition(borderInfo, borderInfo.restoredSplitterPosition, referenceDimension)
                                  : borderInfo.freeDimension,
                collapsed: !!borderInfo.isCollapsed,
                expanded: !!borderInfo.isExpanded
            };
            if (borderInfo.proportion) {
                state.portion = borderInfo.proportion;
            }
            return state;
        },

        _restoreBorderState: function (region, state, isSameSize, referenceSize) {
            var freeDimension;
            var borderInfo = this._borderInfo[region];
            if (borderInfo) {
                var saveAnimation = this.animationEnabled;
                this.animationEnabled = false;
                if (borderInfo.isResizable && !borderInfo.followOpposite) {
                    if (isSameSize) {
                        this.setFreeDimension(region, state.freeDimension);
                        freeDimension = state.freeDimension;
                    } else {
                        var proportion = state.portion || state.proportion;
                        this.setProportion(region, proportion === undefined ? DEFAULT_PROPORTION : proportion);
                        freeDimension = Math.floor(this._getProportion(borderInfo) * referenceSize);
                        // TODO: Should subtract splitter overhead from referenceSize but may not be known yet
                    }
                }
                var wasCollapsed = state.collapsed || state.isCollapsed;
                if (borderInfo.isCollapsible && borderInfo.isCollapsed !== wasCollapsed) {
                    if (wasCollapsed) {
                        this._collapse(borderInfo);
                    } else {
                        this._restore(borderInfo);
                    }
                }
                if (wasCollapsed) {
                    if (borderInfo.initComplete) {
                        borderInfo.restoredSplitterPosition = this._computeSplitterPositionFromFreeDimension(borderInfo, freeDimension, referenceSize);
                    } else {
                        // Defer the splitter position computation until needed splitter metrics have been initialized
                        borderInfo.restoredSplitterPosition = lang.hitch(this, "_computeSplitterPositionFromFreeDimension", borderInfo, freeDimension, referenceSize);
                    }
                    borderInfo.restoredReferenceDimension = referenceSize;
                }
                borderInfo.isExpanded = state.expanded;
                this.animationEnabled = saveAnimation;
            }
        },

        _getProportion: function (borderInfo) {
            return borderInfo && (borderInfo.proportion || borderInfo.portion === 0) ? borderInfo.proportion : DEFAULT_PROPORTION;
        },

        _getDimensionFromStyle: function (borderInfo) {
            var property = borderInfo.splitterOrientation === "H" ? "height" : "width";
            // The following does not consistently get the right value for our purposes:
            // return domStyle.get(borderInfo.widget.domNode, property);
            return borderInfo.widget.domNode.style[property];
        },

        _getBorderThickness: function (borderInfo) {
            var extents = domGeometry.getBorderExtents(borderInfo.widget.domNode);
            return borderInfo.splitterOrientation === "H" ? extents.h : extents.w;
        },

        /**
         * Positions the child widgets
         *
         * This method overrides the inherited layout method
         *
         * @instance
         */
        layout: function () {
            if (!this._started || !this.domNode) {
                return;
            }
            this._layoutChildren();
        },

        _isBorderRegion: function (region) {
            return region === "left" || region === "right" || region === "top" || region === "bottom";
        },

        _oppositeRegion: function (region) {
            switch (region) {
            case "top":
                return "bottom";
            case "bottom":
                return "top";
            case "left":
                return "right";
            case "right":
                return "left";
            }
            return "center";
        },

        _oppositeBorderInfo: function (borderInfo) {
            return this._borderInfo[this._oppositeRegion(borderInfo.region)];
        },

        _layoutChildren: function () {
            this._updateLayoutConstraints();

            // Get available space
            var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
            if (!contentBox || contentBox.h === 0 || contentBox.w === 0) {
                return false;
            }
            var that = this;
            var centerBox = lang.clone(contentBox);
            var totalWidth = centerBox.w;
            var totalHeight = centerBox.h;
            var borderBox, clipBox, splitterBox, collapsedBox;
            var boxes = {};
            var isAdjustable = {};
            var adjustment = {};
            var minAdjustment = {};
            var maxAdjustment = {};
            var portion = {};
            var info, excess, i;
            var splitterOverhead = this._computeSplitterOverhead();
            var isDraggingSplitter = Object.keys(this._dragInfo).length > 0;
            var freeDimension;

            // Sort the border info by layoutOrder
            var orderedInfo = [];
            var regions = Object.keys(this._borderInfo);
            var expandedRegion;
            for (i = 0; i < regions.length; i++) {
                info = this._borderInfo[regions[i]];
                if (info.initComplete) {
                    orderedInfo.push(info);
                    if (info.isExpanded && !info.isRestoring) {
                        expandedRegion = info.region;
                    }
                }
            }
            var infoToRestore = [];
            if (expandedRegion || this._isCenterCollapsed()) {
                var adjustOrder = function (region) {
                    var info = that._borderInfo[region];
                    if (info) {
                        info.layoutOrder += 2;
                        infoToRestore.push(info);
                    }
                };
                // Tweak the order when there is an expanded region or the center is collapsed
                if (this.design === "sidebar" && (expandedRegion === "left" || expandedRegion === "right" ||
                        (this._centerSize && this._centerSize.w < this.minimumCenterWidth))) {
                    ["left", "right"].forEach(adjustOrder);
                } else if (this.design === "headline" && (expandedRegion === "top" || expandedRegion === "bottom" ||
                        (this._centerSize && this._centerSize.h < this.minimumCenterHeight))) {
                    ["top", "bottom"].forEach(adjustOrder);
                }
            }
            orderedInfo.sort(function (i1, i2) {
                // Order by layout order and favor left/top over bottom/right when orders are same.
                // This way if there is no center the left/top will be sized first and the right/bottom
                // can simply use the remaining space.
                return (2 * i1.layoutOrder + ((i1.region === "left" || i1.region === "top") ? 0 : 1)) -
                       (2 * i2.layoutOrder + ((i2.region === "left" || i2.region === "top") ? 0 : 1));
            });
            infoToRestore.forEach(function (info) {
                info.layoutOrder -= 2;
            });

            // Compute boxes for each border widget, adjusting the center box along the way.
            for (i = 0; i < orderedInfo.length; i++) {
                info = orderedInfo[i];
                borderBox = lang.clone(centerBox);
                splitterBox = lang.clone(centerBox);
                collapsedBox = lang.clone(centerBox);
                if (info.unconstrainedFreeDimension) {
                    // If the border child was shrunk due to total size constraints, restore its size, unless
                    // the user is dragging the splitter.
                    if (isDraggingSplitter) {
                        delete info.unconstrainedFreeDimension;
                    } else {
                        info.freeDimension = info.unconstrainedFreeDimension;
                    }
                }
                switch (info.region) {
                    case "top":
                        if (isDraggingSplitter && this._portionsAreStale) {
                            this._updatePortion(info, totalHeight, splitterOverhead.h);
                        }
                        freeDimension = this._computeFreeDimension(info, totalHeight, splitterOverhead.h);
                        borderBox.h = freeDimension;
                        centerBox.t += borderBox.h;
                        centerBox.h -= borderBox.h;
                        if (info.splitterNode) {
                            splitterBox.t = centerBox.t - info.preSplitterOverlap;
                            splitterBox.h = info.splitterThickness;
                            centerBox.t += info.visibleSplitterThickness;
                            centerBox.h -= info.visibleSplitterThickness;
                        }
                        if (info.isCollapsed) {
                            collapsedBox.h = this._getCollapsedThickness(info);
                        }
                        break;
                    case "bottom":
                        if (isDraggingSplitter && this._portionsAreStale) {
                            this._updatePortion(info, totalHeight, splitterOverhead.h);
                        }
                        freeDimension = this._computeFreeDimension(info, totalHeight, splitterOverhead.h);
                        borderBox.h = freeDimension;
                        borderBox.t = centerBox.t + centerBox.h - borderBox.h;
                        centerBox.h -= borderBox.h;
                        if (info.splitterNode) {
                            splitterBox.t = borderBox.t - info.preSplitterOverlap - info.visibleSplitterThickness;
                            splitterBox.h = info.splitterThickness;
                            centerBox.h -= info.visibleSplitterThickness;
                        }
                        if (info.isCollapsed) {
                            collapsedBox.h = this._getCollapsedThickness(info);
                            collapsedBox.t = contentBox.t + contentBox.h - collapsedBox.h;
                        }
                        break;
                    case "left":
                        if (isDraggingSplitter && this._portionsAreStale) {
                            this._updatePortion(info, totalWidth, splitterOverhead.w);
                        }
                        freeDimension = this._computeFreeDimension(info, totalWidth, splitterOverhead.w);
                        borderBox.w = freeDimension;
                        centerBox.l += borderBox.w;
                        centerBox.w -= borderBox.w;
                        if (info.splitterNode) {
                            splitterBox.l = centerBox.l - info.preSplitterOverlap;
                            splitterBox.w = info.splitterThickness;
                            centerBox.l += info.visibleSplitterThickness;
                            centerBox.w -= info.visibleSplitterThickness;
                        }
                        if (info.isCollapsed) {
                            collapsedBox.w = this._getCollapsedThickness(info);
                        }
                        break;
                    case "right":
                        if (isDraggingSplitter && this._portionsAreStale) {
                            this._updatePortion(info, totalWidth, splitterOverhead.w);
                        }
                        freeDimension = this._computeFreeDimension(info, totalWidth, splitterOverhead.w);
                        borderBox.w = freeDimension;
                        borderBox.l = centerBox.l + centerBox.w - borderBox.w;
                        centerBox.w -= borderBox.w;
                        if (info.splitterNode) {
                            splitterBox.l = borderBox.l - info.preSplitterOverlap - info.visibleSplitterThickness;
                            splitterBox.w = info.splitterThickness;
                            centerBox.w -= info.visibleSplitterThickness;
                        }
                        if (info.isCollapsed) {
                            collapsedBox.w = this._getCollapsedThickness(info);
                            collapsedBox.l = contentBox.l + contentBox.w - collapsedBox.w;
                        }
                        break;
                }
                boxes[info.region] = {};
                boxes[info.region].borderBox = borderBox;
                boxes[info.region].splitterBox = splitterBox;
                boxes[info.region].collapsedBox = collapsedBox;
                isAdjustable[info.region] = info.isResizable && info.isProportional && !info.isCollapsed;
                minAdjustment[info.region] = Math.max(info.minSize, info.minAutoSize) - freeDimension;
                maxAdjustment[info.region] = info.maxSize - freeDimension;
            }
            if (isDraggingSplitter) {
                this._portionsAreStale = false;
            }

            // Redistribute any surplus/deficit to the borders as needed.
            // There are three situations in which this is needed:
            //    1) When the center contains a non-proportionally sized child
            //    2) when the center has been collapsed
            //    3) when the container is reduced to the point where the border children must shrink, even if they are
            //       not configured to be sized proportionally.
            var accommodateCenter = this._centerWidget && (this._isCenterProportional === false || this._collapsedCenterWidget)
                                    && this._centerSize && !this._isCenterExpanded && !isDraggingSplitter;
            excess = 0;

            if ((!this._collapsedCenterWidget && this._centerWidget && centerBox.w < this.minimumCenterWidth) || centerBox.w < -1) {
                if (!isDraggingSplitter) {
                    excess = centerBox.w - this.minimumCenterWidth;
                }
            } else if (accommodateCenter) {
                excess = centerBox.w - this._centerSize.w
            }
            if (excess >= 0) {
                if (this._borderInfo.left) {
                    delete this._borderInfo.left.unconstrainedFreeDimension;
                }
                if (this._borderInfo.right) {
                    delete this._borderInfo.right.unconstrainedFreeDimension;
                }
            }
            var prepareToAdjust = function (region) {
                var info = that._borderInfo[region];
                if (info) {
                    isAdjustable[region] = !info.isCollapsed && info.isResizable && !that._dragInfo[region];
                    // Remember the border child's size when "forced" shrinkage began.
                    if (isAdjustable[region] && info.unconstrainedFreeDimension === undefined) {
                        info.unconstrainedFreeDimension = info.freeDimension;
                    }
                }
            };
            if (excess !== 0) {
                if (excess < 0) {
                    prepareToAdjust("left");
                    prepareToAdjust("right");
                }
                adjustment.left = excess;
                adjustment.right = excess;
                if (isAdjustable.left && isAdjustable.right) {
                    portion.left = this._borderInfo.left.portion || 0.5;
                    portion.right = this._borderInfo.right.portion || 0.5;
                    adjustment.left = excess * portion.left / (portion.left + portion.right);
                    adjustment.right = excess - adjustment.left;
                }
                if (isAdjustable.left) {
                    adjustment.left = Math.max(minAdjustment.left, Math.min(maxAdjustment.left, adjustment.left));
                    this._borderInfo.left.freeDimension += adjustment.left;
                    boxes.left.borderBox.w += adjustment.left;
                    centerBox.l += adjustment.left;
                    centerBox.w -= adjustment.left;
                    boxes.left.splitterBox.l += adjustment.left;
                }
                if (isAdjustable.right) {
                    adjustment.right = Math.max(minAdjustment.right, Math.min(maxAdjustment.right, adjustment.right));
                    this._borderInfo.right.freeDimension += adjustment.right;
                    boxes.right.borderBox.w += adjustment.right;
                    boxes.right.borderBox.l -= adjustment.right;
                    centerBox.w -= adjustment.right;
                    boxes.right.splitterBox.l -= adjustment.right;
                }
                if (this.design === "sidebar" && (isAdjustable.left || isAdjustable.right)) {
                    // Match the top and bottom horizontal bounds with the center
                    // This should be generalized to work with any border prioritization
                    if (boxes.top && this._borderInfo.top.layoutOrder > 0) {
                        boxes.top.borderBox.l = centerBox.l;
                        boxes.top.borderBox.w = centerBox.w;
                        boxes.top.splitterBox.l = centerBox.l;
                        boxes.top.splitterBox.w = centerBox.w;
                    }
                    if (boxes.bottom && this._borderInfo.bottom.layoutOrder > 0) {
                        boxes.bottom.borderBox.l = centerBox.l;
                        boxes.bottom.borderBox.w = centerBox.w;
                        boxes.bottom.splitterBox.l = centerBox.l;
                        boxes.bottom.splitterBox.w = centerBox.w;
                    }
                }
            }

            excess = 0;
            if ((!this._collapsedCenterWidget && this._centerWidget && centerBox.h < this.minimumCenterHeight) || centerBox.h < -1) {
                if (!isDraggingSplitter) {
                    excess = centerBox.h - this.minimumCenterHeight;
                }
            } else if (accommodateCenter) {
                excess = centerBox.h - this._centerSize.h
            }
            if (excess >= 0) {
                if (this._borderInfo.top) {
                    delete this._borderInfo.top.unconstrainedFreeDimension;
                }
                if (this._borderInfo.bottom) {
                    delete this._borderInfo.bottom.unconstrainedFreeDimension;
                }
            }
            if (excess !== 0) {
                if (excess < 0) {
                    prepareToAdjust("top");
                    prepareToAdjust("bottom");
                }
                adjustment.top = excess;
                adjustment.bottom = excess;
                if (isAdjustable.top && isAdjustable.bottom) {
                    portion.top = this._borderInfo.top.portion || 0.5;
                    portion.bottom = this._borderInfo.bottom.portion || 0.5;
                    adjustment.top = excess * portion.top / (portion.top + portion.bottom);
                    adjustment.bottom = excess - adjustment.top;
                }
                if (isAdjustable.top) {
                    adjustment.top = Math.max(minAdjustment.top, Math.min(maxAdjustment.top, adjustment.top));
                    this._borderInfo.top.freeDimension += adjustment.top;
                    boxes.top.borderBox.h += adjustment.top;
                    centerBox.t += adjustment.top;
                    centerBox.h -= adjustment.top;
                    boxes.top.splitterBox.t += adjustment.top;
                }
                if (isAdjustable.bottom) {
                    adjustment.bottom = Math.max(minAdjustment.bottom, Math.min(maxAdjustment.bottom, adjustment.bottom));
                    this._borderInfo.bottom.freeDimension += adjustment.bottom;
                    boxes.bottom.borderBox.h += adjustment.bottom;
                    boxes.bottom.borderBox.t -= adjustment.bottom;
                    centerBox.h -= adjustment.bottom;
                    boxes.bottom.splitterBox.t -= adjustment.bottom;
                }
                if (this.design === "headline" && (isAdjustable.top || isAdjustable.bottom)) {
                    // Match the left and right vertical bounds with the center
                    // This should be generalized to work with any border prioritization
                    if (boxes.left) {
                        boxes.left.borderBox.t = centerBox.t;
                        boxes.left.borderBox.h = centerBox.h;
                        boxes.left.splitterBox.t = centerBox.t;
                        boxes.left.splitterBox.h = centerBox.h;
                    }
                    if (boxes.right) {
                        boxes.right.borderBox.t = centerBox.t;
                        boxes.right.borderBox.h = centerBox.h;
                        boxes.right.splitterBox.t = centerBox.t;
                        boxes.right.splitterBox.h = centerBox.h;
                    }
                }
            }

            // Position the widgets per the above computations
            for (i = 0; i < orderedInfo.length; i++) {
                info = orderedInfo[i];
                borderBox = boxes[info.region].borderBox;
                splitterBox = boxes[info.region].splitterBox;
                collapsedBox = boxes[info.region].collapsedBox;
                clipBox = {
                    t : 0,
                    l : 0,
                    w : contentBox.l + contentBox.w,
                    h : contentBox.t + contentBox.h
                };
                switch (info.region) {
                    case "top":
                        if (borderBox.h < info.minSize) {
                            excess = info.minSize - borderBox.h;
                            borderBox.t -= excess;
                            borderBox.h  = info.minSize;
                            clipBox.t = excess;
                        } else {
                            clipBox = null;
                        }
                        break;
                    case "bottom":
                        if (borderBox.h < info.minSize) {
                            clipBox.h = borderBox.h;
                            borderBox.h  = info.minSize;
                        } else {
                            clipBox = null;
                        }
                        break;
                    case "left":
                        if (borderBox.w < info.minSize) {
                            excess = info.minSize - borderBox.w;
                            borderBox.l -= excess;
                            borderBox.w  = info.minSize;
                            clipBox.l = excess;
                        } else {
                            clipBox = null;
                        }
                        break;
                    case "right":
                        if (borderBox.w < info.minSize) {
                            clipBox.w = borderBox.w;
                            borderBox.w  = info.minSize;
                        } else {
                            clipBox = null;
                        }
                        break;
                }
                LayoutUtils.setSize(info.widget, borderBox);
                if (info.isCollapsed) {
                    LayoutUtils.setSize(this._getCollapsedWidget(info), collapsedBox);
                }
                if (info.isResizable) {
                    domStyle.set(info.widget.domNode, clipBox === null ? {clip: "auto"} : LayoutUtils.formatClipStyle(clipBox));
                }
                if (info.splitterNode) {
                    domGeometry.setMarginBox(info.splitterNode, splitterBox);
                    // Store the splitter box because on IE a subsequent getMarginBox call won't necessarily return
                    // the exact values that were just set.
                    info.splitterBox = splitterBox;
                }

                if (info.properties && info.properties.freeDimension !== info.freeDimension  && info.freeDimension !== undefined) {
                    info.properties.set("freeDimension", info.freeDimension);
                }
            }

            if (this._centerWidget) {
                if (this._updateCenterWidget(centerBox, contentBox) && !this.isAnimating()) {
                    // If the center widget changed make another layout pass since the desired child layout order may be affected.
                    setTimeout(lang.hitch(this, this._layoutChildren), 0);
                }
                LayoutUtils.setSize(this._centerWidget, centerBox);
                if (this._collapsedCenterWidget) {
                    LayoutUtils.setSize(this._collapsedCenterWidget, centerBox);
                    this._collapsedCenterWidget.layout();
                }
                if (!this._centerSize || isDraggingSplitter) {
                    this._centerSize = centerBox;
                }
            }

            return true;
        },

        _updateCenterWidget: function (centerBox, contentBox) {
            if (this._isCenterCollapsible()) {
                var collapsedSize = this._centerProperties.getCollapsedSize();
                var isCollapsedWidth = centerBox.w <= collapsedSize.w;
                var isCollapsedHeight = centerBox.h <= collapsedSize.h;
                var showCollapsedWidget = isCollapsedWidth || isCollapsedHeight;
                var showingCollapsedWidget = !!this._collapsedCenterWidget;
                if (showCollapsedWidget !== showingCollapsedWidget) {
                    if (showCollapsedWidget) {
                        // Add the collapsed widget and hide the normal center widget
                        // First determine which edge along the center's collapsed axis is nearest
                        var edge;
                        if (isCollapsedWidth) {
                            var leftDistance = centerBox.l - contentBox.l;
                            var rightDistance = contentBox.w - centerBox.w - leftDistance;
                            edge = rightDistance > leftDistance ? "left" : "right";
                        } else {
                            var topDistance = centerBox.t - contentBox.t;
                            var bottomDistance = contentBox.h - centerBox.h - topDistance;
                            edge = bottomDistance > topDistance ? "top" : "bottom";
                        }
                        this._switchToCollapsedCenter(edge);
                    } else {
                        this._switchToNormalCenter();
                    }
                    return true;
                }
            }
            return false;
        },

        _switchToCollapsedCenter: function(edge) {
            this._collapsedCenterWidget = this._centerProperties.getCollapsedWidget(edge);
            this._collapsedCenterWidget.__edge = edge;
            _LayoutWidget.prototype.addChild.apply(this, [this._collapsedCenterWidget]);
            domClass.add(this._centerWidget.domNode, "hideAllChildren");
            this._collapsedCenterWidget.startup();
            this._centerProperties.set(LayoutChildProperty.IS_COLLAPSED, true);
        },

        _switchToNormalCenter: function() {
            // Remove collapsed widget and restore visibility of normal center widget
            _LayoutWidget.prototype.removeChild.apply(this, [this._collapsedCenterWidget]);
            domClass.remove(this._centerWidget.domNode, "hideAllChildren");
            if (!this._collapsedCenterWidget.saveForReuse) {
                this._collapsedCenterWidget.destroyRecursive();
            }
            delete this._collapsedCenterWidget;
            this._centerProperties.set(LayoutChildProperty.IS_COLLAPSED, false);
        },

        _isCenterCollapsible: function () {
            return this._centerProperties && this._centerProperties.getCollapsedWidget;
        },

        _isCenterCollapsed: function() {
            return !!(this._centerWidget && this._collapsedCenterWidget);
        },

        _updateLayoutConstraints: function () {
            if (!this._started) {return;}

            var centerAvailable, collapsed, followOpposite, fillAvailable;
            if (this._borderInfo.left || this._borderInfo.right) {
                // Of the second clause below one might ask how can the center be expanded if there is no center widget.
                // This situation arises when a state with the center expanded is restored prior to adding the center widget
                // (see geck 1854045)
                centerAvailable = !this._centerWidget && !this._isCenterExpanded &&
                                  (this.design === "headline" || !(this._borderInfo.top || this._borderInfo.bottom));
                if (this._borderInfo.right && this._borderInfo.right.isResizable) {
                    collapsed = this._borderInfo.right.isCollapsed || this._borderInfo.right.isCollapsing || this._borderInfo.right.isRestoring;
                    // If there are right and left children but no center, hide the right child's splitter
                    followOpposite = !!(centerAvailable && this._borderInfo.left) && !collapsed;
                    // Also hide the splitter if there is neither a center nor an opposite
                    fillAvailable = !!(centerAvailable && !this._borderInfo.left) && !collapsed;
                    if (followOpposite !== this._borderInfo.right.followOpposite ||
                        fillAvailable  !== this._borderInfo.right.fillAvailable) {
                        this._borderInfo.right.followOpposite = followOpposite;
                        this._borderInfo.right.fillAvailable  = fillAvailable;
                        delete this._borderInfo.right.freeDimension;
                        if (this._borderInfo.left) {
                            delete this._borderInfo.left.freeDimension;
                        }
                        if (fillAvailable) {
                            this._setCollapsed(this._borderInfo.right, false);
                        }
                        this._setSplitterVisible(this._borderInfo.right, !(followOpposite || fillAvailable));
                    }
                }
                if (this._borderInfo.left && this._borderInfo.left.isResizable) {
                    collapsed = this._borderInfo.left.isCollapsed || this._borderInfo.left.isCollapsing || this._borderInfo.left.isRestoring;
                    fillAvailable = !!(centerAvailable && !this._borderInfo.right) && !collapsed;
                    if (fillAvailable !== this._borderInfo.left.fillAvailable) {
                        this._borderInfo.left.fillAvailable  = fillAvailable;
                        if (fillAvailable) {
                            this._setCollapsed(this._borderInfo.left, false);
                        }
                        delete this._borderInfo.left.freeDimension;
                        this._setSplitterVisible(this._borderInfo.left, !fillAvailable);
                    }
                }
            }
            if (this._borderInfo.bottom || this._borderInfo.top) {
                centerAvailable = !this._centerWidget && !this._isCenterExpanded &&
                                  (this.design === "sidebar" || !(this._borderInfo.left || this._borderInfo.right));
                if (this._borderInfo.bottom && this._borderInfo.bottom.isResizable) {

                    collapsed = this._borderInfo.bottom.isCollapsed || this._borderInfo.bottom.isCollapsing || this._borderInfo.bottom.isRestoring;
                    // If there are top and bottom children but no center, hide the bottom child's splitter
                    followOpposite = !!(centerAvailable && this._borderInfo.top) && !collapsed;
                    // Also hide the splitter if there is neither a center nor an opposite
                    fillAvailable = !!(centerAvailable && !this._borderInfo.top) && !collapsed;
                    if (followOpposite !== this._borderInfo.bottom.followOpposite ||
                        fillAvailable  !== this._borderInfo.bottom.fillAvailable) {
                        this._borderInfo.bottom.followOpposite = followOpposite;
                        this._borderInfo.bottom.fillAvailable  = fillAvailable;
                        delete this._borderInfo.bottom.freeDimension;
                        if (this._borderInfo.top) {
                            delete this._borderInfo.top.freeDimension;
                        }
                        this._setSplitterVisible(this._borderInfo.bottom, !(followOpposite || fillAvailable));
                    }
                }
                if (this._borderInfo.top && this._borderInfo.top.isResizable) {
                    collapsed = this._borderInfo.top.isCollapsed || this._borderInfo.top.isCollapsing || this._borderInfo.top.isRestoring;
                    fillAvailable = !!(centerAvailable && !this._borderInfo.bottom) &&!collapsed;
                    if (fillAvailable !== this._borderInfo.top.fillAvailable) {
                        this._borderInfo.top.fillAvailable  = fillAvailable;
                        if (fillAvailable) {
                            this._setCollapsed(this._borderInfo.top, false);
                        }
                        delete this._borderInfo.top.freeDimension;
                        this._setSplitterVisible(this._borderInfo.top, !fillAvailable);
                    }
                }
            }
        },

        _setSplitterVisible: function (borderInfo, visible) {
            if (borderInfo.splitterNode) {
                domStyle.set(borderInfo.splitterNode, {visibility: visible ? "visible" : "hidden"});
            }
        },

        _setCollapsed: function (borderInfo, collapsed) {
            borderInfo.isCollapsed = collapsed;
            // Apply CSS to hide collapsed child.  Without this Chrome has a tendency to shift the window content in order to
            // show portions of the out-of-view child.  See geck 1322076.
            if (collapsed) {
                domClass.add(borderInfo.widget.domNode, "collapsedChild");
            } else {
                domClass.remove(borderInfo.widget.domNode, "collapsedChild");
            }

            if (borderInfo.properties) {
                borderInfo.properties.set(LayoutChildProperty.IS_COLLAPSED, collapsed);
                if (collapsed) {
                    borderInfo.properties.set(LayoutChildProperty.IS_SHOWING, false);
                } else if (LayoutUtils.isShowing(this.domNode)) {
                    borderInfo.properties.set(LayoutChildProperty.IS_SHOWING, true);
                }
            }
            var collapsedWidget = this._getCollapsedWidget(borderInfo);
            if (collapsed) {
                domStyle.set(collapsedWidget.domNode, "position", "absolute");
                _LayoutWidget.prototype.addChild.apply(this, [collapsedWidget]);
            } else {
                _LayoutWidget.prototype.removeChild.apply(this, [collapsedWidget]);
            }
        },

        _setExpanded: function (borderInfo, expanded) {
            borderInfo.isExpanded = expanded;
            if (borderInfo.properties) {
                borderInfo.properties.set(LayoutChildProperty.IS_EXPANDED, expanded);
            }
        },

        _setCenterExpanded: function (expanded) {
            this._isCenterExpanded = expanded;
            if (this._centerProperties) {
                this._centerProperties.set(LayoutChildProperty.IS_EXPANDED, expanded);
            }
        },

        _createBorderInfo: function (region, widget) {
            return {
                region             : region,
                widget             : widget,
                hasSplitter        : false,
                isResizable        : false,
                splitterOrientation: (region === "top" || region === "bottom") ? "H" : "V",
                minSize            : DEFAULT_MINIMUM_SIZE,
                minAutoSize        : 0,
                maxSize            : DEFAULT_MAXIMUM_SIZE,
                isCollapsible      : false,
                layoutOrder        : (this.design === "headline" && (region === "top" || region === "bottom")) ||
                                     (this.design === "sidebar"  && (region === "left" || region === "right")) ? 0 : 1,
                isProportional     : false,
                proportion         : DEFAULT_PROPORTION,
                collapsedWidget    : widget.collapsedWidget,
                fillAvailable      : false,
                followOpposite     : false
            };
        },

        _getInfoFromWidget: function (widget, borderInfo) {
            if (widget.splitter !== undefined) {
                borderInfo.hasSplitter = widget.splitter;
            } else if (widget.hasSplitter !== undefined) {
                borderInfo.hasSplitter = widget.hasSplitter;
            }
            if (widget.collapsible !== undefined) {
                borderInfo.isCollapsible = widget.collapsible;
            } else if (widget.isCollapsibled !== undefined) {
                borderInfo.isCollapsible = widget.isCollapsible;
            }
            if (widget.resizable !== undefined) {
                borderInfo.isResizable = widget.resizable;
            } else if (widget.isResizable !== undefined) {
                borderInfo.isResizable = widget.isResizable;
            } else {
                borderInfo.isResizable = borderInfo.hasSplitter;
            }
            if (widget.minSize !== undefined) {
                borderInfo.minSize = widget.minSize;
            }
            if (widget.minAutoSize !== undefined) {
                borderInfo.minAutoSize = widget.minAutoSize;
            }
            if (widget.maxSize !== undefined) {
                borderInfo.maxSize = widget.maxSize;
            }
            if (widget.layoutOrder !== undefined) {
                borderInfo.layoutOrder = widget.layoutOrder;
            }
        },

        _bindChildProperties: function (borderInfo, properties) {
            var that = this;
            var collapseWatch, expandWatch;
            // Check for existence of watch rather than instanceof Stateful since the later check may fail if
            // properties are used across window contexts.
            if (!borderInfo.properties && properties.watch && properties.set && properties.get) {
                borderInfo.properties = properties;
                collapseWatch = properties.watch(LayoutChildProperty.IS_COLLAPSED, function (name, oldValue, newValue) {
                    if (that._inRestore) {
                        return;
                    }
                    // Effect collapse change
                    if (newValue !== borderInfo.isCollapsed) {
                        if (newValue) {
                            that._collapse(borderInfo);
                        } else {
                            that._restore(borderInfo);
                        }
                    }
                });
                expandWatch = properties.watch(LayoutChildProperty.IS_EXPANDED, function (name, oldValue, newValue) {
                    if (that._inRestore) {
                        return;
                    }
                    // Effect expand change
                    if (newValue !== borderInfo.isExpanded) {
                        if (newValue) {
                            that.expand(borderInfo.region);
                        } else {
                            that.restore(borderInfo.region);
                        }
                    }
                });
                borderInfo.unbindChildProperties = function () {
                    if (collapseWatch) {
                        collapseWatch.remove();
                    }
                    if (expandWatch) {
                        expandWatch.remove();
                    }
                    delete borderInfo.properties;
                    delete borderInfo.unbindChildProperties;
                };
            }
        },

        _bindCenterProperties: function (properties) {
            var that = this;
            var expandWatch;
            if (!this._centerProperties && properties.watch && properties.set && properties.get) {
                this._centerProperties = properties;
                expandWatch = properties.watch(LayoutChildProperty.IS_EXPANDED, function (name, oldValue, newValue) {
                    // Effect expand change
                    if (newValue !== that._isCenterExpanded) {
                        if (newValue) {
                            that.expand("center");
                        } else {
                            that.restore("center");
                        }
                    }
                });
                this._unbindCenterProperties = function () {
                    expandWatch.remove();
                    delete that._centerProperties;
                    delete that._unbindCenterProperties;
                };
            }
        },

        _getProperties: function (region) {
            if (region === "center") {
                return this._centerProperties;
            } else {
                var borderInfo = this._borderInfo[region];
                if (borderInfo) {
                    return borderInfo.properties;
                }
            }
            return null;
        },

        _computeSplitterOverhead: function () {
            var w = 0;
            var h = 0;
            var info = this._borderInfo;
            Object.keys(this._borderInfo).forEach(function (region) {
                if (info[region.hasSplitter]) {
                    if (info[region].splitterOrientation === "H") {
                        h += info[region].visibleSplitterThickness;
                    } else {
                        w += info[region].visibleSplitterThickness;
                    }
                }
            });
            return {w: w, h: h};
        },

        _computeFreeDimension: function (borderInfo, totalDimension, splitterOverhead) {
            var availDimension = Math.max(1, totalDimension - splitterOverhead);
            var preferredDimension;

            // If dragging use dragged splitter position to determine dimension
            var dragInfo = this._dragInfo[borderInfo.region];
            if (dragInfo) {
                borderInfo.freeDimension = this._computeFreeDimensionFromSplitterPosition(borderInfo, dragInfo.splitterPosition, totalDimension);
                this._enforceFreeDimensionLimits(borderInfo);
                return borderInfo.freeDimension;
            }
            if (borderInfo.isCollapsed) {
                // Normally the free dimension will have been set upon collapse but if the border was already collapsed
                // at startup then it needs to be initialized here (see geck 1463729).
                if (borderInfo.freeDimension === undefined) {
                    borderInfo.freeDimension = this._getCollapsedThickness(borderInfo);
                }
                return borderInfo.freeDimension;
            }
            var oppositeInfo = this._oppositeBorderInfo(borderInfo);
            var minCenterSize = 0;
            if (borderInfo.isExpanded) {
                if (this._centerWidget) {
                    if (this._isCenterCollapsed()) {
                        minCenterSize = borderInfo.region === "left" || borderInfo.region === "right" ? this._centerProperties.getCollapsedSize().w : this._centerProperties.getCollapsedSize().h;
                    } else {
                        minCenterSize = borderInfo.region === "left" || borderInfo.region === "right" ? this.minimumCenterWidth : this.minimumCenterHeight;
                    }
                }
                borderInfo.freeDimension = totalDimension - minCenterSize;
                if (oppositeInfo) {
                    borderInfo.freeDimension -= (oppositeInfo.isCollapsible ? this._getCollapsedThickness(oppositeInfo) : oppositeInfo.freeDimension)
                                              + (oppositeInfo.visibleSplitterThickness ? oppositeInfo.visibleSplitterThickness : 0);
                }
                return borderInfo.freeDimension;
            }
            if (borderInfo.fillAvailable) {
                return totalDimension;
            }
            if (oppositeInfo) {
                if (borderInfo.followOpposite) {
                    borderInfo.freeDimension = totalDimension - oppositeInfo.freeDimension -
                        (oppositeInfo.splitterNode ? oppositeInfo.visibleSplitterThickness : 0);
                    return borderInfo.freeDimension;
                }
                if (oppositeInfo.followOpposite && oppositeInfo.isCollapsed) {
                    borderInfo.freeDimension = totalDimension - borderInfo.postSplitterOverlap - borderInfo.visibleSplitterThickness - borderInfo.preSplitterOverlap;
                    return borderInfo.freeDimension;
                }
            }
            if (!borderInfo.freeDimension || !(borderInfo.isResizable || borderInfo.isProportional)) {

                preferredDimension = borderInfo.initialDimension !== undefined ? borderInfo.initialDimension : this._getDimensionFromStyle(borderInfo);
                if (preferredDimension) {
                    if (preferredDimension.indexOf("%") >= 0) {
                        // Specified as a percentage
                        borderInfo.freeDimension = Math.floor(parseInt(preferredDimension.slice(0, -1), 10) * availDimension / 100);
                    } else {
                        borderInfo.freeDimension = domStyle.toPixelValue(borderInfo.widget.domNode, preferredDimension) +
                                                   this._getBorderThickness(borderInfo);
                        // The style height doesn't include any border thickness.  If it isn't added here the next call to
                        // _getDimensionFromStyle will return a different value (reduced by border thickness).
                    }
                }
                this._updatePortion(borderInfo, totalDimension, splitterOverhead);
            }
            if (borderInfo.isProportional || !borderInfo.freeDimension) {
                var previousDimension = borderInfo.freeDimension;
                // Calculate proportional size
                borderInfo.freeDimension = Math.floor(this._getProportion(borderInfo) * availDimension);
                // Apply minimum size constraint
                if (borderInfo.minSize && borderInfo.freeDimension < borderInfo.minSize) {
                    borderInfo.freeDimension = borderInfo.minSize;
                }
                // Apply alternative minimum size constraint
                if (borderInfo.minAutoSize && borderInfo.freeDimension < borderInfo.minAutoSize) {
                    borderInfo.freeDimension = borderInfo.minAutoSize;
                    if (previousDimension && previousDimension < borderInfo.freeDimension) {
                        borderInfo.freeDimension = previousDimension;
                    }
                }
            }
            this._enforceFreeDimensionLimits(borderInfo);
            return borderInfo.freeDimension;
        },

        _computeFreeDimensionFromSplitterPosition : function (borderInfo, splitterPosition, totalDimension) {
            var extents   = domGeometry.getPadExtents(this.domNode, this.computedStyle);
            var dimension = (borderInfo.region === "left" || borderInfo.region === "top") ?
                            (splitterPosition + borderInfo.preSplitterOverlap) :
                            (totalDimension - splitterPosition - borderInfo.splitterThickness + borderInfo.postSplitterOverlap);
            switch (borderInfo.region) {
            case "left":
                dimension -= extents.l;
                break;
            case "top":
                dimension -= extents.t;
                break;
            case "right":
                dimension += extents.l;
                break;
            case "bottom":
                dimension += extents.t;
                break;
            }
            return dimension;
        },


        _computeSplitterPositionFromFreeDimension: function (borderInfo, freeDimension, totalDimension) {
            var extents  = domGeometry.getPadExtents(this.domNode, this.computedStyle);
            var position = (borderInfo.region === "left" || borderInfo.region === "top") ?
                           (freeDimension - borderInfo.preSplitterOverlap) :
                           (totalDimension - freeDimension - borderInfo.splitterThickness + borderInfo.postSplitterOverlap);

            switch (borderInfo.region) {
                case "left":
                    position += extents.l;
                    break;
                case "top":
                    position += extents.t;
                    break;
                case "right":
                    position -= extents.l;
                    break;
                case "bottom":
                    position -= extents.t;
                    break;
            }
            return position;
        },

        _enforceFreeDimensionLimits: function (borderInfo) {
            if (borderInfo.maxSize !== undefined && borderInfo.freeDimension > borderInfo.maxSize) {
                borderInfo.freeDimension = borderInfo.maxSize;
            }
            // Only enforce minSize if not collapsible
            if (!borderInfo.isCollapsible && borderInfo.minSize !== undefined && borderInfo.freeDimension < borderInfo.minSize) {
                borderInfo.freeDimension = borderInfo.minSize;
            }
        },

        _updatePortion: function (borderInfo, totalDimension, splitterOverhead) {
            var availDimension;
            if (borderInfo.freeDimension && !borderInfo.isCollapsed && borderInfo.isProportional && !borderInfo.fillAvailable) {
                availDimension = Math.max(1, totalDimension - splitterOverhead);
                borderInfo.proportion = borderInfo.freeDimension / availDimension;
            }
        },

        _collapse: function (borderInfo) {
            if (borderInfo.isCollapsed || !borderInfo.isCollapsible) {
                return;
            }
            if (!borderInfo.initComplete) {
                borderInfo.collapsePending = true;
                return;
            }

            if (!this._centerWidget) {
                // When there is not center widget, a child will follow that opposing it (if any).
                // In order to collapse, this following relationship must be severed temporarily.
                // The following achieves this.
                borderInfo.isCollapsing = true;
                this._updateLayoutConstraints();
            }

            // If the border being collapsed is slaved to its opposite, initiate a drag on the opposite border
            var opposite = borderInfo.followOpposite;
            if (opposite) {
                borderInfo = this._oppositeBorderInfo(borderInfo);
            }
            // Treat the collapse as an automated drag
            var dragInfo = this._dragInfo[borderInfo.region];
            if (!dragInfo) {
                dragInfo = this._initDragInfo(borderInfo, 0, 0);
                this._dragInfo[borderInfo.region] = dragInfo;
            }
            dragInfo.done = true;
            this._doCollapse(dragInfo, opposite);
        },

        _restore: function (borderInfo) {
            if (!(borderInfo.isCollapsed || borderInfo.isExpanded)) {
                return;
            }

            // If the border being restored is slaved to its opposite, initiate a drag on the opposite border
            var opposite = borderInfo.followOpposite;
            if (opposite) {
                borderInfo = this._oppositeBorderInfo(borderInfo);
            }

            // Treat the restore as an automated drag
            var dragInfo = this._dragInfo[borderInfo.region];
            if (!dragInfo) {
                dragInfo = this._initDragInfo(borderInfo, 0, 0);
                this._dragInfo[borderInfo.region] = dragInfo;
            }
            dragInfo.done = true;
            this._doRestore(dragInfo, opposite);
        },

        _restoreCenter: function (fromRegion) {
            if (!this._centerSize || !this._preCollapseCenterSize) {
                return;
            }
            // Move the appropriate splitter to restore the center size
            var that = this;
            var resizeRegion = this._oppositeRegion(fromRegion);
            var resizeInfo = this._borderInfo[resizeRegion];
            var adjustment;
            switch (fromRegion) {
                case "left":
                    adjustment = this._preCollapseCenterSize.w - this._centerSize.w;
                    break;
                case "top":
                    adjustment = this._preCollapseCenterSize.h - this._centerSize.h;
                    break;
                case "right":
                    adjustment = this._centerSize.w - this._preCollapseCenterSize.w;
                    break;
                case "bottom":
                    adjustment = this._centerSize.h - this._preCollapseCenterSize.h;
                    break;
            }
            var dragInfo = this._initDragInfo(resizeInfo, 0, 0);
            this._dragInfo[resizeRegion] = dragInfo;
            var newPosition = Math.min(Math.max(dragInfo.splitterPosition + adjustment, dragInfo.lowerLimit), dragInfo.upperLimit);
            if (resizeInfo.isExpanded) {
                this._setExpanded(resizeInfo, false);
            }
            var finisher = function () {
                dragInfo.animation = null;
                dragInfo.splitterPosition = newPosition;
                that._animationEnded();
                resizeInfo.proportion = resizeInfo.preCollapseCenterProportion;
                that.layout();
                delete that._dragInfo[resizeInfo.region];
                that.emit(LayoutContainerEvent.STATE_CHANGED);
            };
            if (!(this.animationEnabled && LayoutUtils.isAnimationEnabled())) {
                finisher();
                this._animationStarted();  // Counteract finisher
            } else {
                this._animationStarted();
                dragInfo.animation = new fx.Animation({
                    curve: [dragInfo.splitterPosition, newPosition],
                    onAnimate : function (value) {
                        dragInfo.splitterPosition = Math.floor(value);
                        that.layout();
                    },
                    onEnd: finisher
                }).play();
            }
        },

        _beginSplitterDrag: function (borderInfo, e) {
            // Initialize starting drag state
            if ((mouse.isLeft(e) || (e.type === "touchstart")) && !borderInfo.isRestoring) {
                // If drag is already in progress simply return (this can happen if the user double clicks on the
                // collapsed splitter (see geck 1337382)
                if (this._dragInfo[borderInfo.region]) {
                    return;
                }
                // Remember the center size so it can subsequently be restored if the drag collapses it.
                if (!this._isCenterCollapsed() && this._centerSize) {
                    this._preCollapseCenterSize = this._centerSize;
                }
                var dragInfo = this._initDragInfo(borderInfo, e.pageX, e.pageY);
                this._dragInfo[borderInfo.region] = dragInfo;

                // Attach listeners
                dragInfo.connections = [
                    on(this.ownerDocument, touch.move, lang.hitch(this, "_continueSplitterDrag", dragInfo)),
                    on(this.ownerDocument, touch.release, lang.hitch(this, "_endSplitterDrag", dragInfo))
                ];
                SelectionBlocker.blockUntilRelease(this.domNode);
            }
            LayoutUtils.stopEvent(e);
        },

        _initDragInfo: function (borderInfo, x, y) {
            var dragInfo = {borderInfo : borderInfo};
            var oppositeInfo = this._oppositeBorderInfo(borderInfo);
            var box = domGeometry.getContentBox(this.domNode, this.computedStyle);
            var splitterBox = borderInfo.splitterBox || domGeometry.getMarginBox(borderInfo.splitterNode);
            var extents = domGeometry.getPadExtents(this.domNode, this.computedStyle);
            var padding;
            var totalSize;
            var isCollapsed = borderInfo.isCollapsed || (oppositeInfo && oppositeInfo.followOpposite && oppositeInfo.isCollapsed);
            var minCenterSize = borderInfo.region === "left" || borderInfo.region === "right" ? this.minimumCenterWidth : this.minimumCenterHeight;
            if (this._centerProperties && this._centerProperties.getCollapsedSize) {
                var collapsedCenterSize = this._centerProperties.getCollapsedSize();
                collapsedCenterSize = borderInfo.region === "left" || borderInfo.region === "right" ? collapsedCenterSize.w : collapsedCenterSize.h;
            }
            if (borderInfo.splitterOrientation === "V") {
                dragInfo.startPoint = x;
                dragInfo.splitterPosition = splitterBox.l;
                dragInfo.initialPosition  = splitterBox.l;
                dragInfo.lastReferenceDimension = box.w;
                padding   = extents.l;
                totalSize = box.w;
                if (!isCollapsed && !borderInfo.isExpanded) {
                    borderInfo.restoredSplitterPosition = splitterBox.l;
                    borderInfo.restoredReferenceDimension = box.w;
                }
            } else {
                dragInfo.startPoint = y;
                dragInfo.splitterPosition = splitterBox.t;
                dragInfo.initialPosition  = splitterBox.t;
                dragInfo.lastReferenceDimension = box.h;
                padding   = extents.t;
                totalSize = box.h;
                if (!isCollapsed && !borderInfo.isExpanded) {
                    borderInfo.restoredSplitterPosition = splitterBox.t;
                    borderInfo.restoredReferenceDimension = box.h;
                }
            }
            // Compute drag limits
            if (borderInfo.region === "left" || borderInfo.region === "top") {
                dragInfo.lowerLimit = padding + this._getCollapsedThickness(borderInfo) - borderInfo.preSplitterOverlap;
                dragInfo.upperLimit = totalSize + padding;
                if (oppositeInfo) {
                    if (oppositeInfo.followOpposite) {
                        dragInfo.upperLimit -= this._getCollapsedThickness(oppositeInfo) + borderInfo.splitterThickness - borderInfo.postSplitterOverlap;
                        dragInfo.oppositeCollapsePosition = dragInfo.upperLimit;
                    } else {
                        dragInfo.upperLimit -= oppositeInfo.freeDimension;
                        if (collapsedCenterSize) {
                            dragInfo.centerCollapseThreshold = dragInfo.upperLimit - minCenterSize + COLLAPSE_THRESHOLD;
                            dragInfo.upperLimit -= collapsedCenterSize;
                        } else if (this._centerWidget) {
                            dragInfo.upperLimit -= minCenterSize;
                        }
                        dragInfo.expandPosition = dragInfo.upperLimit - borderInfo.visibleSplitterThickness;
                        if (oppositeInfo.isCollapsible) {
                            dragInfo.expandPosition += oppositeInfo.freeDimension - this._getCollapsedThickness(oppositeInfo);
                        }
                    }
                } else {
                    if (collapsedCenterSize) {
                        dragInfo.centerCollapseThreshold = dragInfo.upperLimit - minCenterSize + COLLAPSE_THRESHOLD;
                        dragInfo.upperLimit -= collapsedCenterSize;
                    } else if (this._centerWidget) {
                        dragInfo.upperLimit -= minCenterSize;
                    }
                    dragInfo.expandPosition = dragInfo.upperLimit;
                }
                dragInfo.collapsePosition = dragInfo.lowerLimit;
            } else {
                dragInfo.lowerLimit = padding + (oppositeInfo ? oppositeInfo.freeDimension : 0) - borderInfo.preSplitterOverlap;
                if (collapsedCenterSize) {
                    dragInfo.centerCollapseThreshold = dragInfo.lowerLimit + minCenterSize - COLLAPSE_THRESHOLD;
                    dragInfo.lowerLimit += collapsedCenterSize;
                } else if (this._centerWidget) {
                    dragInfo.lowerLimit += minCenterSize;
                }
                dragInfo.upperLimit = padding + totalSize - this._getCollapsedThickness(borderInfo) - borderInfo.splitterThickness + borderInfo.postSplitterOverlap;
                dragInfo.collapsePosition = dragInfo.upperLimit;
                dragInfo.expandPosition = dragInfo.lowerLimit;
                if (oppositeInfo && oppositeInfo.isCollapsible) {
                    dragInfo.expandPosition += this._getCollapsedThickness(oppositeInfo) - oppositeInfo.freeDimension;
                }
            }
            dragInfo.lastPoint = dragInfo.startPoint;
            return dragInfo;
        },

        _continueSplitterDrag: function (dragInfo, e) {
            var borderInfo = dragInfo.borderInfo;
            var oppositeInfo;
            var box = domGeometry.getContentBox(this.domNode, this.computedStyle);
            var newPosition;
            var newFreeDimension;
            var offset;
            var totalDimension = borderInfo.splitterOrientation === "V" ? box.w : box.h;
            var newPoint = borderInfo.splitterOrientation === "V" ? e.pageX : e.pageY;
            var change   = newPoint - dragInfo.lastPoint;
            if (borderInfo.region === "right" || borderInfo.region === "bottom") {
                change *= -1;
            } 
            oppositeInfo = this._oppositeBorderInfo(borderInfo);
            dragInfo.lastPoint = newPoint;
            offset = dragInfo.lastPoint - dragInfo.startPoint;
            if (Math.abs(offset) > SPLITTER_MOVE_THRESHOLD && !dragInfo.hasSplitterMoved) {
                dragInfo.hasSplitterMoved = true;
                this._resizeDragStarted();
                this.emit(LayoutContainerEvent.SPLITTER_DRAG_STARTED, borderInfo.region);
            }
            newPosition = dragInfo.initialPosition + offset;
            if (newPosition < dragInfo.lowerLimit) {
                newPosition = dragInfo.lowerLimit;
            } else if (newPosition > dragInfo.upperLimit) {
                newPosition = dragInfo.upperLimit;
            }
            // See if the border child has been reduced to the point of collapse
            if (borderInfo.isCollapsible) {
                newFreeDimension = this._computeFreeDimensionFromSplitterPosition(borderInfo, newPosition, totalDimension);
                if (change > 0 && newFreeDimension > 0) {
                    this._setCollapsed(borderInfo, false);
                }
                if (change < 0 && newFreeDimension > 0 &&  newFreeDimension < borderInfo.freeDimension &&
                        newFreeDimension < (borderInfo.minSize - COLLAPSE_THRESHOLD)) {
                    this._doCollapse(dragInfo);
                }
            }
            // If there is no center, perform the collapse check for the child on the opposite border
            if (oppositeInfo && oppositeInfo.followOpposite && oppositeInfo.isCollapsible) {
                change *= -1;
                newFreeDimension = this._computeFreeDimensionFromSplitterPosition(oppositeInfo, newPosition, totalDimension);
                if (change > 0 && newFreeDimension > 0) {
                    this._setCollapsed(oppositeInfo, false);
                }
                if (change < 0 && newFreeDimension > 0 && newFreeDimension < oppositeInfo.freeDimension &&
                        newFreeDimension < (oppositeInfo.minSize - COLLAPSE_THRESHOLD)) {
                    this._doCollapse(dragInfo, true);
                }
            } else if (dragInfo.centerCollapseThreshold) {
                // If the center is collapsible check or the collapse threshold has been reached.
                // Adjust the splitter position if it has.
                if (offset > 0 && newPosition > dragInfo.centerCollapseThreshold &&
                    (borderInfo.region === "left" || borderInfo.region === "top")) {
                    newPosition = dragInfo.upperLimit;
                }
                if (offset < 0 && newPosition < dragInfo.centerCollapseThreshold &&
                    (borderInfo.region === "right" || borderInfo.region === "bottom")) {
                    newPosition = dragInfo.lowerLimit;
                }
            }

            if (!dragInfo.animation && !borderInfo.isCollapsed && !(oppositeInfo && oppositeInfo.followOpposite && oppositeInfo.isCollapsed) &&
                    newPosition !== dragInfo.splitterPosition) {

                // g2149079: Have atleast 5px gap b/w two opposite vertical splitters.
                // Prevents spliiters of opposite children from overlapping over each other.
                var newSplitterPosition = newPosition;
                if (oppositeInfo && oppositeInfo.hasSplitter && oppositeInfo.splitterOrientation === "V") { // opposite child has a vertical splitter
                    if (borderInfo.region === "left") {
                        var maxNewSplitterPosition = oppositeInfo.splitterBox.l - 5;
                        if (newSplitterPosition > maxNewSplitterPosition) {
                            newSplitterPosition = maxNewSplitterPosition;
                        }
                    } else if (borderInfo.region === "right") {
                        var minNewSplitterPosition = oppositeInfo.splitterBox.l + 5;
                        if (newSplitterPosition < minNewSplitterPosition) {
                            newSplitterPosition = minNewSplitterPosition;
                        }
                    }
                }
                dragInfo.splitterPosition = newSplitterPosition;
                this.layout();
            }
        },

        _endSplitterDrag: function (dragInfo) {
            var borderInfo = dragInfo.borderInfo;
            var oppositeInfo = this._oppositeBorderInfo(borderInfo);

            // Remove drag listeners
            dragInfo.connections.forEach(function (connection) {
                connection.remove();
            });

            // If splitter wasn't moved appreciably just clear the drag info
            if (!dragInfo.hasSplitterMoved) {
                delete this._dragInfo[borderInfo.region];
            } else {
                // Update proportion
                if (!borderInfo.isCollapsed && !(oppositeInfo && oppositeInfo.followOpposite && oppositeInfo.isCollapsed) &&
                    !dragInfo.animation) {
                    if (this._isCenterCollapsed()) {
                        borderInfo.preCollapseCenterProportion = borderInfo.proportion;
                    }
                    var centerBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
                    if (borderInfo.splitterOrientation === "H") {
                        borderInfo.proportion = borderInfo.freeDimension / centerBox.h;
                    } else {
                        borderInfo.proportion = borderInfo.freeDimension / centerBox.w;
                    }
                }

                if (dragInfo.animation) {
                    dragInfo.done = true;
                } else {
                    delete this._dragInfo[borderInfo.region];
                    this._resizeDragEnded();
                    this.emit(LayoutContainerEvent.SPLITTER_DRAG_ENDED, borderInfo.region);
                    this.emit(LayoutContainerEvent.STATE_CHANGED);
                }
            }
        },

        _handleDoubleClick: function (borderInfo, e) {
            // Collapse or restore in response to double click on splitter but not if collapse or restore is in progress (see geck 1337382).
            if (!borderInfo.isCollapsing && !borderInfo.isRestoring) {
                 // Don't collapse/restore in response to double click if there is no center child between this border child
                 // and the opposite border child.
                 var oppositeInfo = this._oppositeBorderInfo(borderInfo);
                 if (!oppositeInfo || !oppositeInfo.followOpposite) {
                     if (borderInfo.isCollapsed) {
                         this._restore(borderInfo);
                     } else {
                         this._collapse(borderInfo);
                     }
                 }
            }
            LayoutUtils.stopEvent(e);
        },

        _doCollapse: function (dragInfo, opposite) {
            if (dragInfo.animation) {
                return;
            }
            var that = this;
            var borderInfo = dragInfo.borderInfo;
            var collapsingInfo = opposite ? that._oppositeBorderInfo(borderInfo) : borderInfo;
            if (opposite || !borderInfo.fillAvailable) {
                borderInfo.preCollapseFreeDimension = borderInfo.freeDimension;
            } else {
                // If the border child has filled all available space due to the lack of a center or
                // opposite widget then when expanded on top it should only fill half of the space.
                var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
                borderInfo.preCollapseFreeDimension = Math.floor((borderInfo.region === "left" || borderInfo.region === "right" ? contentBox.w : contentBox.h) / 2);
            }
            var finisher = function () {
                dragInfo.animation = null;
                dragInfo.splitterPosition = opposite ? dragInfo.oppositeCollapsePosition : dragInfo.collapsePosition;
                if (!that._layoutChildren()) {
                    borderInfo.freeDimension = that._getCollapsedThickness(borderInfo);
                }
                collapsingInfo.isCollapsing = false;
                that._setCollapsed(collapsingInfo, true);
                that._updateExpandedStates();
                that._animationEnded();
                that.layout();
                if (dragInfo.done) {
                    delete that._dragInfo[borderInfo.region];
                    that.emit(LayoutContainerEvent.STATE_CHANGED);
                }
                that.emit(LayoutContainerEvent.SPLITTER_DRAG_ENDED, borderInfo.region);
            };
            collapsingInfo.isCollapsing = true;
            if (!(this.animationEnabled && LayoutUtils.isAnimationEnabled())) {
                finisher();
                this._animationStarted();  // Counteract finisher
            } else {
                this._animationStarted();
                dragInfo.splitterStartPosition = dragInfo.splitterPosition;
                dragInfo.animation = new fx.Animation({
                    curve: [0.0, 1.0],
                    onAnimate : function (value) {
                        var collapsePosition = opposite ? dragInfo.oppositeCollapsePosition : dragInfo.collapsePosition;
                        var diff = collapsePosition - dragInfo.splitterStartPosition;
                        dragInfo.splitterPosition = dragInfo.splitterStartPosition + Math.floor(value * diff);
                        that.layout();
                    },
                    onEnd: finisher
                }).play();
            }
        },

        _doExpand: function (dragInfo) {
            if (dragInfo.animation) {
                return;
            }
            var that = this;
            var borderInfo = dragInfo.borderInfo;
            this._preCollapseCenterSize = this._centerSize;
            var finisher = function () {
                dragInfo.animation = null;
                dragInfo.splitterPosition = dragInfo.expandPosition;
                that._setExpanded(borderInfo, true);
                that._animationEnded();
                that.layout();
                if (dragInfo.done) {
                    delete that._dragInfo[borderInfo.region];
                    that.emit(LayoutContainerEvent.STATE_CHANGED);
                }
                that.emit(LayoutContainerEvent.SPLITTER_DRAG_ENDED, borderInfo.region);
            };
            if (!(this.animationEnabled && LayoutUtils.isAnimationEnabled())) {
                finisher();
                this._animationStarted();  // Counteract finisher
            } else {
                this._animationStarted();
                dragInfo.splitterStartPosition = dragInfo.splitterPosition;
                dragInfo.animation = new fx.Animation({
                    curve: [0.0, 1.0],
                    onAnimate : function (value) {
                        var diff = dragInfo.expandPosition - dragInfo.splitterStartPosition;
                        dragInfo.splitterPosition = dragInfo.splitterStartPosition + Math.floor(value * diff);
                        that.layout();
                    },
                    onEnd: finisher
                }).play();
            }
        },

        _doRestore: function (dragInfo, opposite) {
            if (dragInfo.animation) {
                return;
            }
            var that = this;
            var borderInfo = dragInfo.borderInfo;
            borderInfo.isRestoring = true;

            // Move the splitter back to its pre-collapse location, unless the container has been resized
            // in the mean time.  In that case choose the restore location proportionally.
            var box = domGeometry.getContentBox(this.domNode, this.computedStyle);
            var referenceDimension = borderInfo.splitterOrientation === "V" ? box.w : box.h;
            var restoredPosition;
            var regions, i, proportion;
            if (borderInfo.restoredSplitterPosition) {
                restoredPosition = borderInfo.restoredSplitterPosition;
                if (referenceDimension !== borderInfo.restoredReferenceDimension) {
                    restoredPosition *= referenceDimension;
                    restoredPosition /= borderInfo.restoredReferenceDimension;
                }
            }
            if (!restoredPosition) {
                proportion = (borderInfo.region === "left" || borderInfo.region === "top") ? DEFAULT_PROPORTION : (1 - DEFAULT_PROPORTION);
                restoredPosition = Math.floor(proportion * referenceDimension);
            }
            if (opposite) {
                this._setCollapsed(this._oppositeBorderInfo(borderInfo), false);
            } else {
                this._setCollapsed(borderInfo, false);
            }
            this._clearExpandedStates();
            var finisher = function () {
                dragInfo.animation = null;
                dragInfo.splitterPosition = restoredPosition;
                that._animationEnded();
                borderInfo.isExpanded = false;  // Must be done before layout
                that.layout();
                if (dragInfo.done) {
                    delete that._dragInfo[borderInfo.region];
                    that.emit(LayoutContainerEvent.STATE_CHANGED);
                }
                borderInfo.isRestoring = false;
            };
            if (!(this.animationEnabled && LayoutUtils.isAnimationEnabled())) {
                finisher();
                this._animationStarted();  // Counteract finisher
            } else {
                this._animationStarted();
                dragInfo.animation = new fx.Animation({
                    curve: [dragInfo.splitterPosition, restoredPosition],
                    onAnimate : function (value) {
                        dragInfo.splitterPosition = Math.floor(value);
                        that.layout();
                    },
                    onEnd: finisher
                }).play();
            }
        },

        _getCollapsedWidget: function(borderInfo) {
            if (!borderInfo.collapsedWidget) {
                var collapsedWidget = new ContentPane({content: borderInfo.splitterOrientation === "H" ? CollapsedTemplateH : CollapsedTemplateV});
                collapsedWidget.isDefaultCollapsedWidget = true;
                domClass.add(collapsedWidget.domNode, "collapsedBorderChild" + borderInfo.splitterOrientation);
                dom.setSelectable(collapsedWidget.domNode, false);
                LayoutUtils.preventFocus(collapsedWidget.domNode);

                // Connect listener's to support expand and drag
                var dragBorderInfo = borderInfo.followOpposite ? this._oppositeBorderInfo(borderInfo) : borderInfo;
                collapsedWidget.own(on(collapsedWidget.domNode, touch.press,lang.hitch(this, "_beginSplitterDrag", dragBorderInfo)));
                collapsedWidget.own(on(collapsedWidget.domNode, "contextmenu", function (event) {event.preventDefault();}));

                borderInfo.collapsedWidget = collapsedWidget;
            }
            return borderInfo.collapsedWidget;
        },

        _getCollapsedThickness: function(borderInfo) {
            if (!borderInfo.collapsedThickness) {
                var collapsedWidget = this._getCollapsedWidget(borderInfo);
                var collapsedBox;
                // Temporarily place the collapsed widget to facilitate measurement
                domStyle.set(collapsedWidget.domNode, {visibility: "hidden"});
                domConstruct.place(collapsedWidget.domNode, this.domNode);
                collapsedBox = domGeometry.getContentBox(collapsedWidget.domNode);
                borderInfo.collapsedThickness = borderInfo.splitterOrientation === "H" ? collapsedBox.h : collapsedBox.w;
                this.domNode.removeChild(collapsedWidget.domNode);
                domStyle.set(collapsedWidget.domNode, {visibility: "visible"});
            }
            return borderInfo.collapsedThickness;
        },

        _updateExpandedStates: function () {
            // See if all but one region is collapsed, in which case that one region should be considered expanded
            var nonCollapsedCount = 0;
            var collapsibleBorderCount = 0;
            var nonCollapsedRegion;
            var regions, i, borderInfo;
            if (this._centerWidget) {
                nonCollapsedCount += 1;
                nonCollapsedRegion = "center";
            }
            regions = Object.keys(this._borderInfo);
            for (i = 0; i < regions.length && nonCollapsedCount <= 1; i = i + 1) {
                borderInfo = this._borderInfo[regions[i]];
                if (borderInfo.isCollapsible) {
                    collapsibleBorderCount++;
                    if (!borderInfo.isCollapsed) {
                        nonCollapsedCount += 1;
                        nonCollapsedRegion = regions[i];
                    }
                }
            }
            if (nonCollapsedCount === 1) {
                if (nonCollapsedRegion === "center") {
                    if (!this._isCenterExpanded && collapsibleBorderCount) {
                        this._setCenterExpanded(true);
                        this._regionsCollapsedUponCenterExpand = regions;
                    }
                } else {
                    borderInfo = this._borderInfo[nonCollapsedRegion];
                    if (!borderInfo.isExpanded) {
                        this._setExpanded(borderInfo, true);
                        borderInfo.regionsCollapsedUponExpand = regions.filter(function (region) {
                            return region !== nonCollapsedRegion;
                        });
                    }
                }
            }
        },

        _clearExpandedStates: function () {
            this._setCenterExpanded(false);
            var regions = Object.keys(this._borderInfo);
            for (var i = 0; i < regions.length; i++) {
                var info = this._borderInfo[regions[i]];
                if (!info.isRestoring) {
                    this._setExpanded(info, false);
                }
            }
        },

        _animationStarted: function () {
            this._activeAnimationCount++;
        },

        _animationEnded: function () {
            this._activeAnimationCount--;
        },

        isAnimating: function() {
            return this._activeAnimationCount > 0;
        },

        _resizeDragStarted: function () {
            this._resizeDragCount++;
        },

        _resizeDragEnded: function () {
            this._resizeDragCount--;
        },

        isResizingViaDrag: function () {
            return this._resizeDragCount > 0;
        },

        destroyRecursive: function () {
            // Destroy collapsed widgets that aren't currently showing
            var regions, i, borderInfo;
            regions = Object.keys(this._borderInfo);
            for (i = 0; i < regions.length; i++) {
                borderInfo = this._borderInfo[regions[i]];
                if (borderInfo.collapsedWidget && !borderInfo.isCollapsed) {
                    borderInfo.collapsedWidget.destroyRecursive();
                }
            }
            this.inherited(arguments);
        }
    });
});
